// Feature: features/nma-claims-medical-necessity-tab.md
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Input,
  Button,
  Code,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  HStack,
  VStack,
  Collapse,
  Alert,
  AlertIcon,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from './shared';

const SUBTYPE_CHART_COLORS = {
  CPT_DX_CROSSWALK_EXCLUSION: '#E53E3E',
  EXPERIMENTAL_UNPROVEN: '#DD6B20',
  PRINCIPAL_DX_REJECTED: '#805AD5',
  POST_REVIEW_CLINICAL_DOC: '#D53F8C',
  MEDICARE_LCD_NCD: '#3182CE',
  GENERAL_MEDICAL_NECESSITY: '#319795',
};

const SIGNAL_CHART_COLORS = {
  EXPLICIT_CARC: '#2563EB',
  RARC_IMPLIED: '#7C3AED',
  TEXT_OR_FOOTNOTE_IMPLIED: '#D97706',
};

const DIAGNOSTIC_CHART_COLORS = {
  BURIED_HIGH_ACUITY_OR_TRUNCATED: '#DC2626',
  CPT_MODALITY_POLICY_MISMATCH: '#EA580C',
  REVEDY_DX_DIVERGENCE: '#7C3AED',
  ROUTINE_SPINE_POLICY_EXCLUSION: '#2563EB',
  CLINICAL_DOC_OR_OTHER_MED_NEC: '#0D9488',
};

const DEFAULT_EEG_SCENARIO_ORDER = [
  '1. UHC/AARP Medicare Advantage (Rules #14/#24 Leak)',
  '2. UHC Commercial (POS/EPO/HMO/PPO — Rule #34 Ended Oct 16)',
  '3. UHC Delegated MA Carve-Outs (WellMed / Intermountain / OptumCare)',
  '4. UHC Regional Commercial (HPN / Sierra / Kelsey Seybold)',
  '5. Humana / Aetna / HealthSpring Medicare Advantage',
  '6. Aetna & Cigna Commercial (95822 Outperforms 95955)',
  '7. BCBS & Ambetter Commercial (95955 Outperforms 95822)',
  '8. Traditional Medicare (Rule #5 -> 95822)',
  '9. Other Payers (Tricare / VA / Medicaid / WC)',
];

const DEFAULT_EEG_SCENARIO_NOTES = {
  '1. UHC/AARP Medicare Advantage (Rules #14/#24 Leak)':
    "CRITICAL LEAK: Active Rules #14 & #24 (MEDICARE ADVANTAGE PLAN -> use_95822_for_eeg) force 95822 on UHC/AARP MA, where 95822 denies 84.5% (PI-16/M51 & PI-96/N163) even while companion CPTs pay! Fix: Carve out 'UHC/AARP' from Rules #14/#24.",
  '2. UHC Commercial (POS/EPO/HMO/PPO — Rule #34 Ended Oct 16)':
    'FIXED ON OCT 16 (Except HMO #229): Rule #34 (payer=UHC -> use_95822_for_eeg) was ended Oct 16 after 83.5% of 95822 lines denied (PI-16/M50 & Optum Code AU). Note: Carrier Rule #229 (United Healthcare HMO) is still active and should be ended.',
  '3. UHC Delegated MA Carve-Outs (WellMed / Intermountain / OptumCare)':
    "KEEP 95822 (90.2% Paid!): Delegated MA plans (WellMed 97.6% paid, Intermountain 87.1% paid, OptumCare 71.4% paid) reimburse 95822. Carving out only 'UHC/AARP' preserves 95822 on these plans.",
  '4. UHC Regional Commercial (HPN / Sierra / Kelsey Seybold)':
    'PAYS BOTH (~90% Paid): HPN/Sierra Health & Life and UHC Kelsey Seybold reimburse both 95955 (90.0%) and 95822 (90.5%).',
  '5. Humana / Aetna / HealthSpring Medicare Advantage':
    'KEEP 95822 (90.9% Paid!): Humana MA, Aetna MA, and HealthSpring MA reimburse 95822 at 90.9% under Medicare Advantage rules.',
  '6. Aetna & Cigna Commercial (95822 Outperforms 95955)':
    'ADD use_95822_for_eeg FOR AETNA & CIGNA: 95955 denies 80.9% ($283K via Aetna PI-55/N623 & Cigna CO-16/M76), whereas 95822 pays 66.7%+.',
  '7. BCBS & Ambetter Commercial (95955 Outperforms 95822)':
    'KEEP 95955 (78.7% Paid vs 67.9% on 95822): BCBS and Ambetter reimburse 95955 at a higher rate than 95822.',
  '8. Traditional Medicare (Rule #5 -> 95822)':
    'KEEP 95822 (92.0% Paid!): Traditional Medicare MACs reimburse 95822 at 92.0% under Rule #5.',
  '9. Other Payers (Tricare / VA / Medicaid / WC)':
    "STANDARD RULES (75%-78% Paid): Tricare, VA, Medicaid, and Workers' Comp adjudicate within normal parameters.",
};

const DEFAULT_EEG_SQL_MIGRATION = `BEGIN;

-- 1. End blanket MEDICARE ADVANTAGE PLAN use_95822_for_eeg rules (#14 and duplicate #24)
--    and United Healthcare HMO carrier rule (#229)
UPDATE deterministic_rules
SET effective_end_date = CURRENT_TIMESTAMP
WHERE id IN (14, 24, 229)
  AND org_id = 1
  AND action = 'use_95822_for_eeg'
  AND effective_end_date IS NULL;

-- 2. Re-insert MEDICARE ADVANTAGE PLAN -> use_95822_for_eeg carving out UHC/AARP
--    (Preserves Humana MA, Aetna MA, WellMed, Intermountain, OptumCare, HealthSpring)
INSERT INTO deterministic_rules (org_id, rule_type, target_name, action, effective_start_date, conditions)
VALUES (
    1,
    'dynamic',
    'MA_USE_95822_EXC_UHC_AARP',
    'use_95822_for_eeg',
    CURRENT_TIMESTAMP,
    '{
        "operator": "AND",
        "rules": [
            {"field": "payer", "operator": "==", "value": "MEDICARE ADVANTAGE PLAN"},
            {"field": "carrier", "operator": "NOT CONTAINS", "value": "UHC/AARP"}
        ]
    }'::jsonb
);

-- 3. Add use_95822_for_eeg for Commercial Aetna/Meritain and Cigna/Allegiance
INSERT INTO deterministic_rules (org_id, rule_type, target_name, action, effective_start_date, conditions)
VALUES (
    1,
    'dynamic',
    'AETNA_CIGNA_USE_95822_FOR_EEG',
    'use_95822_for_eeg',
    CURRENT_TIMESTAMP,
    '{
        "operator": "OR",
        "rules": [
            {"field": "payer", "operator": "IN", "value": ["AETNA", "CIGNA"]},
            {"field": "carrier", "operator": "CONTAINS", "value": "AETNA"},
            {"field": "carrier", "operator": "CONTAINS", "value": "MERITAIN"},
            {"field": "carrier", "operator": "CONTAINS", "value": "CIGNA"},
            {"field": "carrier", "operator": "CONTAINS", "value": "ALLEGIANCE"}
        ]
    }'::jsonb
);

COMMIT;`;

const toMondayWeekStr = (dateStr) => {
  if (!dateStr || String(dateStr).length < 10) return '';
  const parts = String(dateStr).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return '';
  const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  const day = dt.getUTCDay();
  const diff = (day + 6) % 7; // Monday = 0
  dt.setUTCDate(dt.getUTCDate() - diff);
  return dt.toISOString().slice(0, 10);
};

const DEFAULT_SUBTYPES = [
  {
    subtype_key: 'CPT_DX_CROSSWALK_EXCLUSION',
    order: 1,
    short_label: 'CPT × DX Crosswalk / Policy',
    label: '1. CPT × Diagnosis Crosswalk / Policy Exclusion',
    code_examples: 'CO-16/M76, CO-11/N657, PI-16/M50-M51, CO-146, Cigna Policy 0509 / Code 181',
    description:
      'Billed CPT is deemed inconsistent with or non-reimbursable for the linked diagnosis pointers (Box 24E) or surgical indication.',
    badge_color: 'red',
  },
  {
    subtype_key: 'EXPERIMENTAL_UNPROVEN',
    order: 2,
    short_label: 'Experimental / Unproven',
    label: '2. Experimental / Investigational / Unproven',
    code_examples: 'CO-55, PI-55, PR-55, CO-56, PR-56 + N623 / N356',
    description:
      'Procedure/modality deemed experimental, investigational, or unproven for the surgical diagnosis (e.g., Aetna CPB 0697 on 95941/95955).',
    badge_color: 'orange',
  },
  {
    subtype_key: 'PRINCIPAL_DX_REJECTED',
    order: 3,
    short_label: 'Principal DX (Pointer A)',
    label: '3. Principal Diagnosis (Pointer A) Rejected',
    code_examples: 'CO-167, RARC MA63, Remark f81',
    description:
      'Payer front-end scrub rejected the primary diagnosis in Box 21A (Pointer A) as non-covered or invalid principal diagnosis.',
    badge_color: 'purple',
  },
  {
    subtype_key: 'POST_REVIEW_CLINICAL_DOC',
    order: 4,
    short_label: 'Post-Review Clinical Doc & UM',
    label: '4. Post-Review Clinical Doc & Utilization Mgmt',
    code_examples: 'PI-B12, PI-96/N163, CO-150, UM0, Optum AU/HP/4A, Peer Review N10',
    description:
      'Service disallowed after clinical chart audit, Utilization Management (UM0), IME (X275), or because medical records did not support the billed code.',
    badge_color: 'pink',
  },
  {
    subtype_key: 'MEDICARE_LCD_NCD',
    order: 5,
    short_label: 'Medicare / MA LCD & NCD',
    label: '5. Medicare / MA LCD or NCD Determination',
    code_examples: 'CO-50/N115, CO-96/N115, CO-11/N115, RARC N386',
    description:
      'Denied under a Medicare Administrative Contractor (MAC) Local Coverage Determination (LCD) or National Coverage Determination (NCD) crosswalk.',
    badge_color: 'blue',
  },
  {
    subtype_key: 'GENERAL_MEDICAL_NECESSITY',
    order: 6,
    short_label: 'General Medical Necessity',
    label: '6. General Medical Necessity Denial',
    code_examples: 'CO-50, PR-50, PI-50, Text-Implied Med-Nec',
    description:
      'General payer medical necessity denial without a specific LCD/principal-DX remark code.',
    badge_color: 'teal',
  },
];

const DEFAULT_SIGNAL_SOURCES = [
  {
    key: 'EXPLICIT_CARC',
    short_label: 'Explicit CARC',
    label: 'Explicit Med-Nec CARC (50, 55, 56, 167, 11, 150, B12)',
    badge_color: 'blue',
  },
  {
    key: 'RARC_IMPLIED',
    short_label: 'RARC-Implied (CO-16 / CO-96)',
    label: 'RARC-Implied on Generic CARC (CO-16/M76, CO-96/N115, M50/M51, N163)',
    badge_color: 'purple',
  },
  {
    key: 'TEXT_OR_FOOTNOTE_IMPLIED',
    short_label: 'No CARC (Footnote / Text)',
    label: 'No CARC — Footnote / Text Implied (Policy 0509 / 181, UM0, Optum AU)',
    badge_color: 'orange',
  },
];

const DEFAULT_DIAGNOSTICS = [
  {
    key: 'BURIED_HIGH_ACUITY_OR_TRUNCATED',
    short_label: 'Buried Tier 1–3 / Ptr Issue',
    label: '1. Buried High-Acuity DX or Pointer Sequencing',
    badge_color: 'red',
  },
  {
    key: 'CPT_MODALITY_POLICY_MISMATCH',
    short_label: 'CPT Policy Mismatch',
    label: '2. CPT / Modality Payer Policy Mismatch',
    badge_color: 'orange',
  },
  {
    key: 'REVEDY_DX_DIVERGENCE',
    short_label: 'Revedy DX Divergence',
    label: '3. Billed DX Diverged from Revedy Coding',
    badge_color: 'purple',
  },
  {
    key: 'ROUTINE_SPINE_POLICY_EXCLUSION',
    short_label: 'Routine Spine Exclusion',
    label: '4. Routine Spine Policy Exclusion (Tier 4/5 Only)',
    badge_color: 'blue',
  },
  {
    key: 'CLINICAL_DOC_OR_OTHER_MED_NEC',
    short_label: 'Clinical Doc / Appeal Review',
    label: '5. Post-Review Doc / Clinical Appeal Review',
    badge_color: 'teal',
  },
];

const MedNecSmallMultipleCard = React.memo(
  ({
    rank,
    entityName,
    totalEobLines,
    totalPaidLines,
    totalMedNecLines,
    medNecPct,
    paidPct,
    weeklySeries,
    groupType,
    onSelectEntity,
    isSelected,
  }) => {
    const syncId = `med_nec_sm_${groupType}_${rank}`;
    return (
      <Card
        variant="outline"
        bg={isSelected ? 'blue.50' : 'white'}
        borderColor={isSelected ? 'blue.400' : 'gray.200'}
        shadow="xs"
        cursor={onSelectEntity ? 'pointer' : 'default'}
        onClick={() => onSelectEntity && onSelectEntity(entityName)}
        _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
      >
        <CardBody p={3}>
          <Box mb={2}>
            <Text fontSize="xs" fontWeight="bold" color="gray.900" noOfLines={1} title={entityName}>
              #{rank} {entityName}
            </Text>
            <HStack spacing={2} mt={0.5} wrap="wrap" fontSize="11px">
              <Text color="gray.700">
                EOB Lines: <b>{totalEobLines.toLocaleString()}</b>
              </Text>
              <Text color="gray.400">|</Text>
              <Text color="red.600" fontWeight="bold">
                ● Med-Nec: {medNecPct}% ({totalMedNecLines.toLocaleString()})
              </Text>
              <Text color="gray.400">|</Text>
              <Text color="green.600" fontWeight="semibold">
                ● Paid: {paidPct}% ({totalPaidLines.toLocaleString()})
              </Text>
            </HStack>
          </Box>

          {/* Top Panel: Med-Nec Denied % & EOB Paid % (0 - 100%) */}
          <Box h="135px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={weeklySeries}
                syncId={syncId}
                margin={{ top: 6, right: 10, left: -18, bottom: 2 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" hide />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tick={{ fontSize: 10, fill: '#1A202C' }}
                  unit="%"
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '11px',
                    padding: '5px 8px',
                  }}
                  labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                  formatter={(val, name, props) => {
                    const p = props?.payload || {};
                    const num = name === 'Med-Nec Denied %' ? p.medNecLines || 0 : p.eobPaidLines || 0;
                    return [
                      val !== null && val !== undefined
                        ? `${val}% (${num.toLocaleString()} / ${(p.eobAdjLines || 0).toLocaleString()} EOB lines)`
                        : '0%',
                      name,
                    ];
                  }}
                  labelFormatter={(wk) => `Week of ${wk}`}
                />
                <Line
                  type="monotone"
                  dataKey="medNecPct"
                  name="Med-Nec Denied %"
                  stroke="#DC2626"
                  strokeWidth={2.3}
                  dot={{ r: 2.5, fill: '#DC2626' }}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="paidPct"
                  name="EOB Paid %"
                  stroke="#16A34A"
                  strokeWidth={2.1}
                  dot={{ r: 2.5, fill: '#16A34A' }}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Bottom Panel: Weekly EOB-Adjudicated Line Volume */}
          <Box h="88px" w="100%" mt={1}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={weeklySeries}
                syncId={syncId}
                margin={{ top: 2, right: 10, left: -18, bottom: 18 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 9, fill: '#1A202C' }}
                  angle={-30}
                  textAnchor="end"
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '11px',
                    padding: '5px 8px',
                  }}
                  labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                  formatter={(val) => [`${Number(val || 0).toLocaleString()} EOB lines`, 'Weekly EOB Lines']}
                  labelFormatter={(wk) => `Week of ${wk}`}
                />
                <Bar
                  dataKey="eobAdjLines"
                  name="Weekly EOB Lines"
                  fill="#CBD5E1"
                  stroke="#64748B"
                  strokeWidth={0.8}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>
    );
  }
);

const EEGScenarioCard = React.memo(
  ({ idx, scenarioName, totalLines, paidPct, medNecPct, weeklySeries, note }) => {
    const syncId = `eeg_scen_sync_${idx}`;
    return (
      <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
        <CardBody p={3}>
          <Box mb={2}>
            <Text fontSize="xs" fontWeight="bold" color="gray.900" noOfLines={1} title={scenarioName}>
              {scenarioName}
            </Text>
            <HStack spacing={2} mt={0.5} wrap="wrap" fontSize="11px">
              <Text color="gray.700">
                Total: <b>{totalLines.toLocaleString()} lines</b>
              </Text>
              <Text color="gray.400">|</Text>
              <Text color="green.600" fontWeight="bold">
                ● Paid: {paidPct}%
              </Text>
              <Text color="gray.400">|</Text>
              <Text color="red.600" fontWeight="bold">
                ● Med-Nec Denied: {medNecPct}%
              </Text>
            </HStack>
          </Box>

          {weeklySeries.length === 0 ? (
            <Box py={8} textAlign="center" color="gray.500" fontSize="xs">
              No EEG lines in active window for this scenario.
            </Box>
          ) : (
            <>
              {/* Top Panel: EEG Med-Nec Denied %, All EEG Denied %, EEG Paid % */}
              <Box h="135px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={weeklySeries}
                    syncId={syncId}
                    margin={{ top: 6, right: 10, left: -18, bottom: 2 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="week" hide />
                    <YAxis
                      domain={[0, 100]}
                      ticks={[0, 25, 50, 75, 100]}
                      tick={{ fontSize: 10, fill: '#1A202C' }}
                      unit="%"
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#1A202C',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        padding: '5px 8px',
                      }}
                      labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                      formatter={(val, name, props) => {
                        const p = props?.payload || {};
                        let num = 0;
                        if (name === 'EEG Med-Nec Denied %') num = p.medNecLines || 0;
                        else if (name === 'All EEG Denied %') num = p.allDeniedLines || 0;
                        else if (name === 'EEG Paid/Allowed %') num = p.paidLines || 0;
                        return [
                          val !== null && val !== undefined
                            ? `${val}% (${num.toLocaleString()} / ${(p.eobLines || 0).toLocaleString()} lines)`
                            : '0%',
                          name,
                        ];
                      }}
                      labelFormatter={(wk) => `Week of ${wk}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="medNecPct"
                      name="EEG Med-Nec Denied %"
                      stroke="#DC2626"
                      strokeWidth={2.4}
                      dot={{ r: 2.5, fill: '#DC2626' }}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="allDeniedPct"
                      name="All EEG Denied %"
                      stroke="#EA580C"
                      strokeWidth={1.8}
                      strokeDasharray="4 3"
                      dot={{ r: 2, fill: '#EA580C' }}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="paidPct"
                      name="EEG Paid/Allowed %"
                      stroke="#16A34A"
                      strokeWidth={2.2}
                      dot={{ r: 2.5, fill: '#16A34A' }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Bottom Panel: Weekly EOB Lines Stacked by 95822 (Blue) vs 95955 (Purple) */}
              <Box h="88px" w="100%" mt={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={weeklySeries}
                    syncId={syncId}
                    margin={{ top: 2, right: 10, left: -18, bottom: 18 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 9, fill: '#1A202C' }}
                      angle={-30}
                      textAnchor="end"
                      interval="preserveStartEnd"
                      minTickGap={16}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#1A202C',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        padding: '5px 8px',
                      }}
                      labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                      formatter={(val, name) => [`${Number(val || 0).toLocaleString()} lines`, name]}
                      labelFormatter={(wk) => `Week of ${wk}`}
                    />
                    <Bar
                      dataKey="lines95822"
                      name="CPT 95822 (Blue)"
                      stackId="eeg_vol"
                      fill="#2563EB"
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="lines95955"
                      name="CPT 95955 (Purple)"
                      stackId="eeg_vol"
                      fill="#9333EA"
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </>
          )}

          {note && (
            <Text fontSize="10px" color="gray.600" mt={1.5} lineHeight="short">
              💡 {note}
            </Text>
          )}
        </CardBody>
      </Card>
    );
  }
);

const NMAMedicalNecessityTab = React.memo(({ data, onOpenClaimByNumber }) => {
  const medNecData = data || {};
  const subtypes = medNecData.subtypes?.length ? medNecData.subtypes : DEFAULT_SUBTYPES;
  const signalSources = medNecData.signal_sources?.length ? medNecData.signal_sources : DEFAULT_SIGNAL_SOURCES;
  const diagnostics = medNecData.diagnostics?.length ? medNecData.diagnostics : DEFAULT_DIAGNOSTICS;
  const allDeniedLines = useMemo(() => medNecData.denied_lines || [], [medNecData.denied_lines]);
  const cptAdjTotals = useMemo(
    () => medNecData.cpt_adjudication_totals || [],
    [medNecData.cpt_adjudication_totals]
  );
  const pointerATierTotals = useMemo(
    () => medNecData.pointer_a_tier_totals || [],
    [medNecData.pointer_a_tier_totals]
  );
  const weeklyBaselines = useMemo(
    () => medNecData.weekly_eob_baselines || [],
    [medNecData.weekly_eob_baselines]
  );
  const dosCohortMaturity = useMemo(
    () => medNecData.dos_cohort_maturity || [],
    [medNecData.dos_cohort_maturity]
  );
  const eegAnalysis = useMemo(() => medNecData.eeg_analysis || {}, [medNecData.eeg_analysis]);

  // Sub-tab state inside Tab 5 (matching the Streamlit console sections)
  const [activeSubTab, setActiveSubTab] = useState('OVERVIEW');

  // Scope & Filter state (defaults to Pro Only + Exclude 95999 + By EOB Week + Exclude Immature Cohorts)
  const [claimTypeScope, setClaimTypeScope] = useState('pro');
  const [exclude95999, setExclude95999] = useState(true);
  const [dateMode, setDateMode] = useState('EOB_WEEK');
  const [excludeImmatureWeeks, setExcludeImmatureWeeks] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [subtypeFilter, setSubtypeFilter] = useState('ALL');
  const [signalSourceFilter, setSignalSourceFilter] = useState('ALL');
  const [diagnosticFilter, setDiagnosticFilter] = useState('ALL');
  const [payerGroupFilter, setPayerGroupFilter] = useState('ALL');
  const [payerFilter, setPayerFilter] = useState('ALL');
  const [cptFilter, setCptFilter] = useState('ALL');
  const [tierFilter, setTierFilter] = useState('ALL');
  const [displayCodeFilter, setDisplayCodeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Sub-Tab 2 (Small Multiples) & Sub-Tab 3 (EEG Scenarios) local view controls
  const [smallMultipleViewLevel, setSmallMultipleViewLevel] = useState('PAYER_GROUP');
  const [selectedEegScenario, setSelectedEegScenario] = useState('ALL_SCENARIOS');
  const [showEegSql, setShowEegSql] = useState(false);
  const [sqlCopied, setSqlCopied] = useState(false);

  // Pagination state for the line-level explorer
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [
    claimTypeScope,
    exclude95999,
    dateMode,
    excludeImmatureWeeks,
    startDate,
    endDate,
    subtypeFilter,
    signalSourceFilter,
    diagnosticFilter,
    payerGroupFilter,
    payerFilter,
    cptFilter,
    tierFilter,
    displayCodeFilter,
    searchQuery,
    pageSize,
  ]);

  const handleResetFilters = () => {
    setClaimTypeScope('pro');
    setExclude95999(true);
    setDateMode('EOB_WEEK');
    setExcludeImmatureWeeks(true);
    setStartDate('');
    setEndDate('');
    setSubtypeFilter('ALL');
    setSignalSourceFilter('ALL');
    setDiagnosticFilter('ALL');
    setPayerGroupFilter('ALL');
    setPayerFilter('ALL');
    setCptFilter('ALL');
    setTierFilter('ALL');
    setDisplayCodeFilter('ALL');
    setSearchQuery('');
  };

  // DOS Cohort Maturity computation for DOS_WEEK mode (identifies recent surgery weeks with <25% EOB completion)
  const dosMaturityInfo = useMemo(() => {
    if (!dosCohortMaturity.length) {
      return { matureDosWeeksSet: null, excludedDosWeeksInfo: [] };
    }
    const byWeek = {};
    dosCohortMaturity.forEach((row) => {
      if (claimTypeScope !== 'all' && row.claim_type !== claimTypeScope) return;
      const wk = row.dos_week;
      if (!wk || wk < '2026-07-27') return;
      if (!byWeek[wk]) {
        byWeek[wk] = { dos_week: wk, total_claims: 0, eob_adj_claims: 0 };
      }
      byWeek[wk].total_claims += Number(row.total_claims || 0);
      byWeek[wk].eob_adj_claims += Number(row.eob_adj_claims || 0);
    });

    const matureSet = new Set();
    const excludedInfo = [];
    Object.values(byWeek)
      .sort((a, b) => a.dos_week.localeCompare(b.dos_week))
      .forEach((r) => {
        const pct = (r.eob_adj_claims / Math.max(r.total_claims, 1)) * 100.0;
        if (pct >= 25.0) {
          matureSet.add(r.dos_week);
        } else if (r.eob_adj_claims > 0) {
          excludedInfo.push(
            `${r.dos_week} (${pct.toFixed(1)}% w/ EOB: ${r.eob_adj_claims}/${r.total_claims}c)`
          );
        }
      });

    return {
      matureDosWeeksSet: matureSet.size > 0 ? matureSet : null,
      excludedDosWeeksInfo: excludedInfo,
    };
  }, [dosCohortMaturity, claimTypeScope]);

  // Lines matching the primary scope toggles (claimTypeScope + exclude95999 + date range)
  const scopedLines = useMemo(() => {
    return allDeniedLines.filter((l) => {
      if (claimTypeScope !== 'all' && l.claim_type !== claimTypeScope) return false;
      if (exclude95999 && l.cpt_code === '95999') return false;
      const targetDate =
        dateMode === 'EOB_WEEK'
          ? String(l.eob_date || l.date_of_service || '').slice(0, 10)
          : String(l.date_of_service || '').slice(0, 10);
      if (startDate && targetDate && targetDate < startDate) return false;
      if (endDate && targetDate && targetDate > endDate) return false;
      return true;
    });
  }, [allDeniedLines, claimTypeScope, exclude95999, dateMode, startDate, endDate]);

  // Available Payer Group, Specific Carrier, and CPT dropdown options within the active scope
  const filterOptions = useMemo(() => {
    const payerGroupCounts = {};
    const payerCounts = {};
    const cptCounts = {};
    scopedLines.forEach((l) => {
      const pg = l.payer_group || 'OTHER PAYERS';
      payerGroupCounts[pg] = (payerGroupCounts[pg] || 0) + 1;
      if (payerGroupFilter !== 'ALL' && pg !== payerGroupFilter) return;
      if (l.payer_name) {
        payerCounts[l.payer_name] = (payerCounts[l.payer_name] || 0) + 1;
      }
      if (l.cpt_code) {
        cptCounts[l.cpt_code] = (cptCounts[l.cpt_code] || 0) + 1;
      }
    });
    const payerGroups = Object.entries(payerGroupCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, cnt]) => ({ name, cnt }));
    const payers = Object.entries(payerCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, cnt]) => ({ name, cnt }));
    const cpts = Object.entries(cptCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([code, cnt]) => ({ code, cnt }));
    return { payerGroups, payers, cpts };
  }, [scopedLines, payerGroupFilter]);

  // Subtype card metrics (computed across scopedLines with non-subtype filters applied)
  const subtypeStats = useMemo(() => {
    const qLower = searchQuery.trim().toLowerCase();
    const baseFiltered = scopedLines.filter((l) => {
      if (signalSourceFilter !== 'ALL' && l.signal_source !== signalSourceFilter) return false;
      if (diagnosticFilter !== 'ALL' && l.diagnostic_key !== diagnosticFilter) return false;
      if (payerGroupFilter !== 'ALL' && (l.payer_group || 'OTHER PAYERS') !== payerGroupFilter) return false;
      if (payerFilter !== 'ALL' && l.payer_name !== payerFilter) return false;
      if (cptFilter !== 'ALL' && l.cpt_code !== cptFilter) return false;
      if (tierFilter !== 'ALL' && l.pointer_a_tier_key !== tierFilter) return false;
      if (displayCodeFilter !== 'ALL' && l.display_code !== displayCodeFilter) return false;
      if (qLower) {
        const hay = `${l.claim_number || ''} ${l.nma_case_no || ''} ${l.patient_name || ''} ${l.payer_name || ''} ${l.cpt_code || ''} ${l.display_code || ''} ${l.description || ''} ${l.pointer_a_code || ''}`.toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      return true;
    });

    const totalLines = baseFiltered.length;
    const totalClaimsSet = new Set();
    let totalDeniedDollars = 0;

    const byKey = {};
    subtypes.forEach((st) => {
      byKey[st.subtype_key] = {
        ...st,
        lines: 0,
        claimsSet: new Set(),
        deniedAmount: 0,
      };
    });

    baseFiltered.forEach((l) => {
      totalClaimsSet.add(l.claim_number);
      totalDeniedDollars += Number(l.denied_amount || 0);
      const bucket = byKey[l.subtype_key];
      if (bucket) {
        bucket.lines += 1;
        bucket.claimsSet.add(l.claim_number);
        bucket.deniedAmount += Number(l.denied_amount || 0);
      }
    });

    const cards = subtypes.map((st) => {
      const b = byKey[st.subtype_key];
      return {
        ...st,
        lines: b.lines,
        claims: b.claimsSet.size,
        deniedAmount: b.deniedAmount,
        sharePct: totalLines > 0 ? Math.round((b.lines / totalLines) * 1000) / 10 : 0,
      };
    });

    return {
      totalLines,
      totalClaims: totalClaimsSet.size,
      totalDeniedDollars,
      cards,
    };
  }, [
    scopedLines,
    subtypes,
    signalSourceFilter,
    diagnosticFilter,
    payerGroupFilter,
    payerFilter,
    cptFilter,
    tierFilter,
    displayCodeFilter,
    searchQuery,
  ]);

  // Fully filtered lines (including subtypeFilter)
  const filteredLines = useMemo(() => {
    const qLower = searchQuery.trim().toLowerCase();
    return scopedLines.filter((l) => {
      if (subtypeFilter !== 'ALL' && l.subtype_key !== subtypeFilter) return false;
      if (signalSourceFilter !== 'ALL' && l.signal_source !== signalSourceFilter) return false;
      if (diagnosticFilter !== 'ALL' && l.diagnostic_key !== diagnosticFilter) return false;
      if (payerGroupFilter !== 'ALL' && (l.payer_group || 'OTHER PAYERS') !== payerGroupFilter) return false;
      if (payerFilter !== 'ALL' && l.payer_name !== payerFilter) return false;
      if (cptFilter !== 'ALL' && l.cpt_code !== cptFilter) return false;
      if (tierFilter !== 'ALL' && l.pointer_a_tier_key !== tierFilter) return false;
      if (displayCodeFilter !== 'ALL' && l.display_code !== displayCodeFilter) return false;
      if (qLower) {
        const hay = `${l.claim_number || ''} ${l.nma_case_no || ''} ${l.patient_name || ''} ${l.payer_name || ''} ${l.cpt_code || ''} ${l.display_code || ''} ${l.description || ''} ${l.pointer_a_code || ''}`.toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      return true;
    });
  }, [
    scopedLines,
    subtypeFilter,
    signalSourceFilter,
    diagnosticFilter,
    payerGroupFilter,
    payerFilter,
    cptFilter,
    tierFilter,
    displayCodeFilter,
    searchQuery,
  ]);

  // Signal Source & Root-Cause Diagnostic counts across filteredLines
  const signalAndDiagnosticStats = useMemo(() => {
    const sigCounts = {};
    const diagCounts = {};
    signalSources.forEach((s) => {
      sigCounts[s.key] = { ...s, lines: 0, claimsSet: new Set(), deniedAmount: 0 };
    });
    diagnostics.forEach((d) => {
      diagCounts[d.key] = { ...d, lines: 0, claimsSet: new Set(), deniedAmount: 0 };
    });

    filteredLines.forEach((l) => {
      if (sigCounts[l.signal_source]) {
        sigCounts[l.signal_source].lines += 1;
        sigCounts[l.signal_source].claimsSet.add(l.claim_number);
        sigCounts[l.signal_source].deniedAmount += Number(l.denied_amount || 0);
      }
      if (diagCounts[l.diagnostic_key]) {
        diagCounts[l.diagnostic_key].lines += 1;
        diagCounts[l.diagnostic_key].claimsSet.add(l.claim_number);
        diagCounts[l.diagnostic_key].deniedAmount += Number(l.denied_amount || 0);
      }
    });

    const total = Math.max(filteredLines.length, 1);
    return {
      signals: signalSources.map((s) => ({
        ...sigCounts[s.key],
        claims: sigCounts[s.key].claimsSet.size,
        sharePct: filteredLines.length ? Math.round((sigCounts[s.key].lines / total) * 1000) / 10 : 0,
      })),
      diagnostics: diagnostics.map((d) => ({
        ...diagCounts[d.key],
        claims: diagCounts[d.key].claimsSet.size,
        sharePct: filteredLines.length ? Math.round((diagCounts[d.key].lines / total) * 1000) / 10 : 0,
      })),
    };
  }, [filteredLines, signalSources, diagnostics]);

  // Weekly Trend Series: Joins weekly EOB denominator baselines with filtered Medical Necessity denied lines
  const weeklyTrendSeries = useMemo(() => {
    const startWk = startDate ? toMondayWeekStr(startDate) : '';
    const endWk = endDate ? toMondayWeekStr(endDate) : '';
    const byWeek = {};

    const ensureWeek = (wk) => {
      if (!byWeek[wk]) {
        byWeek[wk] = {
          week: wk,
          eobAdjLines: 0,
          eobPaidLines: 0,
          allDeniedLines: 0,
          medNecLines: 0,
          CPT_DX_CROSSWALK_EXCLUSION: 0,
          EXPERIMENTAL_UNPROVEN: 0,
          PRINCIPAL_DX_REJECTED: 0,
          POST_REVIEW_CLINICAL_DOC: 0,
          MEDICARE_LCD_NCD: 0,
          GENERAL_MEDICAL_NECESSITY: 0,
          EXPLICIT_CARC: 0,
          RARC_IMPLIED: 0,
          TEXT_OR_FOOTNOTE_IMPLIED: 0,
          BURIED_HIGH_ACUITY_OR_TRUNCATED: 0,
          CPT_MODALITY_POLICY_MISMATCH: 0,
          REVEDY_DX_DIVERGENCE: 0,
          ROUTINE_SPINE_POLICY_EXCLUSION: 0,
          CLINICAL_DOC_OR_OTHER_MED_NEC: 0,
        };
      }
      return byWeek[wk];
    };

    // 1. Accumulate EOB-adjudicated denominator lines per week matching scope, payer, cpt, and date range
    weeklyBaselines.forEach((b) => {
      if (claimTypeScope !== 'all' && b.ct !== claimTypeScope) return;
      if (exclude95999 && b.cpt === '95999') return;
      if (payerGroupFilter !== 'ALL' && (b.pg || 'OTHER PAYERS') !== payerGroupFilter) return;
      if (payerFilter !== 'ALL' && b.payer !== payerFilter) return;
      if (cptFilter !== 'ALL' && b.cpt !== cptFilter) return;

      const wk = dateMode === 'EOB_WEEK' ? b.ew || b.dw : b.dw;
      if (!wk || wk < '2026-07-27') return;
      if (startWk && wk < startWk) return;
      if (endWk && wk > endWk) return;
      if (
        dateMode === 'DOS_WEEK' &&
        excludeImmatureWeeks &&
        dosMaturityInfo.matureDosWeeksSet &&
        !dosMaturityInfo.matureDosWeeksSet.has(wk)
      ) {
        return;
      }

      const slot = ensureWeek(wk);
      slot.eobAdjLines += Number(b.adj_l || 0);
      slot.eobPaidLines += Number(b.paid_l || 0);
      slot.allDeniedLines += Number(b.den_l || 0);
    });

    // 2. Accumulate filtered Medical Necessity denied lines per week across subtypes, signal sources, and diagnostics
    filteredLines.forEach((l) => {
      const rawWk =
        dateMode === 'EOB_WEEK'
          ? l.eob_week || toMondayWeekStr(l.eob_date || l.date_of_service)
          : l.dos_week || toMondayWeekStr(l.date_of_service);
      if (!rawWk || rawWk < '2026-07-27') return;
      if (startWk && rawWk < startWk) return;
      if (endWk && rawWk > endWk) return;
      if (
        dateMode === 'DOS_WEEK' &&
        excludeImmatureWeeks &&
        dosMaturityInfo.matureDosWeeksSet &&
        !dosMaturityInfo.matureDosWeeksSet.has(rawWk)
      ) {
        return;
      }

      const slot = ensureWeek(rawWk);
      slot.medNecLines += 1;
      if (slot[l.subtype_key] !== undefined) {
        slot[l.subtype_key] += 1;
      }
      if (slot[l.signal_source] !== undefined) {
        slot[l.signal_source] += 1;
      }
      if (slot[l.diagnostic_key] !== undefined) {
        slot[l.diagnostic_key] += 1;
      }
    });

    let rows = Object.values(byWeek).filter((r) => r.eobAdjLines > 0 || r.medNecLines > 0);

    // In EOB_WEEK mode, when Exclude Immature Cohorts is checked and viewing all payers/CPTs,
    // filter out initial August pre-steady-state backfill weeks (<200 EOB lines)
    if (
      dateMode === 'EOB_WEEK' &&
      excludeImmatureWeeks &&
      payerGroupFilter === 'ALL' &&
      payerFilter === 'ALL' &&
      cptFilter === 'ALL'
    ) {
      const steadyRows = rows.filter((r) => r.eobAdjLines >= 200);
      if (steadyRows.length > 0) {
        rows = steadyRows;
      }
    }

    return rows
      .sort((a, b) => a.week.localeCompare(b.week))
      .map((r) => {
        const denom = Math.max(r.eobAdjLines, r.medNecLines, 1);
        const hasDenom = r.eobAdjLines > 0 || r.medNecLines > 0;
        const pct = (val) => (hasDenom ? Math.round((val / denom) * 1000) / 10 : null);
        return {
          ...r,
          denom,
          medNecPct: pct(r.medNecLines),
          allDeniedPct: pct(r.allDeniedLines),
          paidPct: pct(r.eobPaidLines),
          CPT_DX_CROSSWALK_EXCLUSION_pct: pct(r.CPT_DX_CROSSWALK_EXCLUSION),
          EXPERIMENTAL_UNPROVEN_pct: pct(r.EXPERIMENTAL_UNPROVEN),
          PRINCIPAL_DX_REJECTED_pct: pct(r.PRINCIPAL_DX_REJECTED),
          POST_REVIEW_CLINICAL_DOC_pct: pct(r.POST_REVIEW_CLINICAL_DOC),
          MEDICARE_LCD_NCD_pct: pct(r.MEDICARE_LCD_NCD),
          GENERAL_MEDICAL_NECESSITY_pct: pct(r.GENERAL_MEDICAL_NECESSITY),
          EXPLICIT_CARC_pct: pct(r.EXPLICIT_CARC),
          RARC_IMPLIED_pct: pct(r.RARC_IMPLIED),
          TEXT_OR_FOOTNOTE_IMPLIED_pct: pct(r.TEXT_OR_FOOTNOTE_IMPLIED),
          BURIED_HIGH_ACUITY_OR_TRUNCATED_pct: pct(r.BURIED_HIGH_ACUITY_OR_TRUNCATED),
          CPT_MODALITY_POLICY_MISMATCH_pct: pct(r.CPT_MODALITY_POLICY_MISMATCH),
          REVEDY_DX_DIVERGENCE_pct: pct(r.REVEDY_DX_DIVERGENCE),
          ROUTINE_SPINE_POLICY_EXCLUSION_pct: pct(r.ROUTINE_SPINE_POLICY_EXCLUSION),
          CLINICAL_DOC_OR_OTHER_MED_NEC_pct: pct(r.CLINICAL_DOC_OR_OTHER_MED_NEC),
        };
      });
  }, [
    weeklyBaselines,
    filteredLines,
    claimTypeScope,
    exclude95999,
    payerGroupFilter,
    payerFilter,
    cptFilter,
    dateMode,
    excludeImmatureWeeks,
    dosMaturityInfo,
    startDate,
    endDate,
  ]);

  // Sub-Tab 2: Payer Group & Top 30 Carrier Small Multiples
  const smallMultipleCards = useMemo(() => {
    const startWk = startDate ? toMondayWeekStr(startDate) : '';
    const endWk = endDate ? toMondayWeekStr(endDate) : '';
    const isPayerGroupMode = smallMultipleViewLevel === 'PAYER_GROUP';
    const maxCards = isPayerGroupMode ? 20 : 30;
    const entitiesMap = {};

    const ensureEntityWeek = (entName, wk) => {
      if (!entitiesMap[entName]) {
        entitiesMap[entName] = {
          entityName: entName,
          totalEobLines: 0,
          totalPaidLines: 0,
          totalMedNecLines: 0,
          byWeek: {},
        };
      }
      if (!entitiesMap[entName].byWeek[wk]) {
        entitiesMap[entName].byWeek[wk] = {
          week: wk,
          eobAdjLines: 0,
          eobPaidLines: 0,
          medNecLines: 0,
        };
      }
      return entitiesMap[entName].byWeek[wk];
    };

    weeklyBaselines.forEach((b) => {
      if (claimTypeScope !== 'all' && b.ct !== claimTypeScope) return;
      if (exclude95999 && b.cpt === '95999') return;
      if (payerGroupFilter !== 'ALL' && (b.pg || 'OTHER PAYERS') !== payerGroupFilter) return;
      if (payerFilter !== 'ALL' && b.payer !== payerFilter) return;
      if (cptFilter !== 'ALL' && b.cpt !== cptFilter) return;

      const wk = dateMode === 'EOB_WEEK' ? b.ew || b.dw : b.dw;
      if (!wk || wk < '2026-07-27') return;
      if (startWk && wk < startWk) return;
      if (endWk && wk > endWk) return;
      if (
        dateMode === 'DOS_WEEK' &&
        excludeImmatureWeeks &&
        dosMaturityInfo.matureDosWeeksSet &&
        !dosMaturityInfo.matureDosWeeksSet.has(wk)
      ) {
        return;
      }
      if (dateMode === 'EOB_WEEK' && excludeImmatureWeeks && wk < '2026-08-31') {
        return;
      }

      const entName = isPayerGroupMode ? b.pg || 'OTHER PAYERS' : b.payer || 'Unknown Payer';
      const wSlot = ensureEntityWeek(entName, wk);
      const adjL = Number(b.adj_l || 0);
      const paidL = Number(b.paid_l || 0);
      wSlot.eobAdjLines += adjL;
      wSlot.eobPaidLines += paidL;
      entitiesMap[entName].totalEobLines += adjL;
      entitiesMap[entName].totalPaidLines += paidL;
    });

    filteredLines.forEach((l) => {
      const rawWk =
        dateMode === 'EOB_WEEK'
          ? l.eob_week || toMondayWeekStr(l.eob_date || l.date_of_service)
          : l.dos_week || toMondayWeekStr(l.date_of_service);
      if (!rawWk || rawWk < '2026-07-27') return;
      if (startWk && rawWk < startWk) return;
      if (endWk && rawWk > endWk) return;
      if (
        dateMode === 'DOS_WEEK' &&
        excludeImmatureWeeks &&
        dosMaturityInfo.matureDosWeeksSet &&
        !dosMaturityInfo.matureDosWeeksSet.has(rawWk)
      ) {
        return;
      }
      if (dateMode === 'EOB_WEEK' && excludeImmatureWeeks && rawWk < '2026-08-31') {
        return;
      }

      const entName = isPayerGroupMode ? l.payer_group || 'OTHER PAYERS' : l.payer_name || 'Unknown Payer';
      const wSlot = ensureEntityWeek(entName, rawWk);
      wSlot.medNecLines += 1;
      entitiesMap[entName].totalMedNecLines += 1;
    });

    return Object.values(entitiesMap)
      .filter((e) => e.totalEobLines > 0)
      .sort((a, b) => b.totalEobLines - a.totalEobLines)
      .slice(0, maxCards)
      .map((e, idx) => {
        const safeDenom = Math.max(e.totalEobLines, 1);
        const weeklySeries = Object.values(e.byWeek)
          .filter((w) => w.eobAdjLines > 0 || w.medNecLines > 0)
          .sort((a, b) => a.week.localeCompare(b.week))
          .map((w) => {
            const wd = Math.max(w.eobAdjLines, w.medNecLines, 1);
            return {
              ...w,
              medNecPct: Math.round((w.medNecLines / wd) * 1000) / 10,
              paidPct: Math.round((w.eobPaidLines / wd) * 1000) / 10,
            };
          });
        return {
          rank: idx + 1,
          entityName: e.entityName,
          totalEobLines: e.totalEobLines,
          totalPaidLines: e.totalPaidLines,
          totalMedNecLines: e.totalMedNecLines,
          medNecPct: ((e.totalMedNecLines / safeDenom) * 100).toFixed(1),
          paidPct: ((e.totalPaidLines / safeDenom) * 100).toFixed(1),
          weeklySeries,
        };
      });
  }, [
    weeklyBaselines,
    filteredLines,
    smallMultipleViewLevel,
    claimTypeScope,
    exclude95999,
    payerGroupFilter,
    payerFilter,
    cptFilter,
    dateMode,
    excludeImmatureWeeks,
    dosMaturityInfo,
    startDate,
    endDate,
  ]);

  // Sub-Tab 3: Intraoperative EEG (95822 vs. 95955) & Rule Scenarios Analytics
  const eegAnalyticsComputed = useMemo(() => {
    const rawEegLines = eegAnalysis.eeg_lines || [];
    const scenarioOrder = eegAnalysis.scenario_order?.length
      ? eegAnalysis.scenario_order
      : DEFAULT_EEG_SCENARIO_ORDER;
    const scenarioNotes = eegAnalysis.scenario_notes || DEFAULT_EEG_SCENARIO_NOTES;
    const sqlMigration = eegAnalysis.sql_migration || DEFAULT_EEG_SQL_MIGRATION;
    const dbRules = eegAnalysis.db_rules || [];

    const scopedEeg = rawEegLines.filter((l) => {
      if (claimTypeScope !== 'all' && l.claim_type !== claimTypeScope) return false;
      const targetDate =
        dateMode === 'EOB_WEEK'
          ? String(l.eob_date || l.dos || '').slice(0, 10)
          : String(l.dos || '').slice(0, 10);
      if (startDate && targetDate && targetDate < startDate) return false;
      if (endDate && targetDate && targetDate > endDate) return false;
      return true;
    });

    const scen1Name = '1. UHC/AARP Medicare Advantage (Rules #14/#24 Leak)';
    const scen2Name = '2. UHC Commercial (POS/EPO/HMO/PPO — Rule #34 Ended Oct 16)';
    const scen3Name = '3. UHC Delegated MA Carve-Outs (WellMed / Intermountain / OptumCare)';
    const scen5Name = '5. Humana / Aetna / HealthSpring Medicare Advantage';
    const scen6Name = '6. Aetna & Cigna Commercial (95822 Outperforms 95955)';
    const scen7Name = '7. BCBS & Ambetter Commercial (95955 Outperforms 95822)';

    const uhcAarp822 = scopedEeg.filter((l) => l.eeg_scenario === scen1Name && l.cpt_code === '95822');
    const uhcComm822 = scopedEeg.filter((l) => l.eeg_scenario === scen2Name && l.cpt_code === '95822');
    const uhcDel822 = scopedEeg.filter((l) => l.eeg_scenario === scen3Name && l.cpt_code === '95822');
    const aetCig955 = scopedEeg.filter((l) => l.eeg_scenario === scen6Name && l.cpt_code === '95955');
    const aetCig822 = scopedEeg.filter((l) => l.eeg_scenario === scen6Name && l.cpt_code === '95822');
    const maKeep822 = scopedEeg.filter(
      (l) => (l.eeg_scenario === scen3Name || l.eeg_scenario === scen5Name) && l.cpt_code === '95822'
    );
    const bcbs955 = scopedEeg.filter((l) => l.eeg_scenario === scen7Name && l.cpt_code === '95955');

    const sumField = (arr, field) => arr.reduce((acc, x) => acc + Number(x[field] || 0), 0);

    const aarpDen = sumField(uhcAarp822, 'is_denied');
    const aarpMn = sumField(uhcAarp822, 'is_med_nec_denied');
    const aarpDenDollars = sumField(uhcAarp822, 'denied_amount');
    const aarpCompPaid = uhcAarp822.filter((l) => l.is_denied && l.companion_paid_on_claim).length;

    const ucommDen = sumField(uhcComm822, 'is_denied');
    const ucommMn = sumField(uhcComm822, 'is_med_nec_denied');
    const ucommDenDollars = sumField(uhcComm822, 'denied_amount');

    const udelPaid = sumField(uhcDel822, 'is_paid');

    const maKeepPaid = sumField(maKeep822, 'is_paid');
    const maKeepPaidDollars = sumField(maKeep822, 'paid_amount');

    const ac955Paid = sumField(aetCig955, 'is_paid');
    const ac955Den = sumField(aetCig955, 'is_denied');
    const ac955Mn = sumField(aetCig955, 'is_med_nec_denied');
    const ac955DenDollars = sumField(aetCig955, 'denied_amount');
    const ac822WinRate =
      aetCig822.length > 0 ? sumField(aetCig822, 'is_paid') / Math.max(aetCig822.length, 1) : 0.7;
    const paidAcLines = scopedEeg.filter((l) => l.eeg_scenario === scen6Name && Number(l.paid_amount || 0) > 0);
    const avgAcPaidPerLine =
      paidAcLines.length > 0 ? sumField(paidAcLines, 'paid_amount') / paidAcLines.length : 350.0;
    const estNewPaidLinesAc = Math.max(0, Math.round(aetCig955.length * ac822WinRate) - ac955Paid);
    const estNewCashAc = Math.round(estNewPaidLinesAc * avgAcPaidPerLine);

    const bcbs955Paid = sumField(bcbs955, 'is_paid');
    const bcbs955PaidDollars = sumField(bcbs955, 'paid_amount');

    const kpis = {
      aarp: {
        tot: uhcAarp822.length,
        den: aarpDen,
        denPct: ((aarpDen / Math.max(uhcAarp822.length, 1)) * 100).toFixed(1),
        denDollars: aarpDenDollars,
      },
      ucomm: {
        tot: uhcComm822.length,
        den: ucommDen,
        denPct: ((ucommDen / Math.max(uhcComm822.length, 1)) * 100).toFixed(1),
        denDollars: ucommDenDollars,
      },
      udel: {
        tot: uhcDel822.length,
        paid: udelPaid,
        paidPct: ((udelPaid / Math.max(uhcDel822.length, 1)) * 100).toFixed(1),
      },
      aetCig: {
        tot: aetCig955.length,
        den: ac955Den,
        denPct: ((ac955Den / Math.max(aetCig955.length, 1)) * 100).toFixed(1),
        denDollars: ac955DenDollars,
      },
    };

    const recImpactRows = [
      {
        priority: '1. UHC/AARP Medicare Advantage (Rules #14 & #24)',
        currentState:
          "ACTIVE LEAK: Blanket payer = 'MEDICARE ADVANTAGE PLAN' Rules #14 & #24 force 95955 -> 95822 on all UHC/AARP MA claims.",
        recommendedChange:
          "End duplicate Rules #14 & #24; insert Dynamic Rule: payer == 'MEDICARE ADVANTAGE PLAN' AND carrier NOT CONTAINS 'UHC/AARP' -> use_95822_for_eeg.",
        deniedLinesEliminated: `${aarpDen} lines (${aarpMn} Med-Nec PI-16/M51)`,
        deniedDollarsEliminated: formatCurrency(aarpDenDollars),
        expectedImpact: `Stops ${aarpDen} guaranteed 95822 denials (on ${aarpCompPaid} claims where companion CPTs paid) while protecting 100% of the ${maKeepPaid}/${maKeep822.length} (${((maKeepPaid / Math.max(maKeep822.length, 1)) * 100).toFixed(1)}%) paid 95822 lines (${formatCurrency(maKeepPaidDollars)} cash) on Humana, Aetna, WellMed & Intermountain MA.`,
      },
      {
        priority: '2. UHC Commercial HMO (United Healthcare HMO Rule #229)',
        currentState:
          "Rule #34 (payer = 'UHC') was ended Oct 16, but Carrier Rule #229 (United Healthcare HMO -> use_95822_for_eeg) is STILL ACTIVE.",
        recommendedChange:
          'End-date Carrier Rule #229 (WHERE id = 229) so all UHC Commercial non-cranial spine cases stay 95955 (and fix inverted NCCI XU Rules #347/#348 -> apply_xu_G0453_95955 / apply_xu_95940_95955).',
        deniedLinesEliminated: `${ucommDen} lines (${ucommMn} Med-Nec PI-16/M50 & Code AU)`,
        deniedDollarsEliminated: formatCurrency(ucommDenDollars),
        expectedImpact:
          "Completes the Oct 16 UHC Commercial fix; eliminates Optum Code AU ('non-intracranial surgery') and PI-16/M50 spine EEG denials while allowing 95955 to adjudicate cleanly alongside G0453-XU.",
      },
      {
        priority: '3. Aetna & Cigna Commercial (95955 -> 95822)',
        currentState:
          'NO RULE: Aetna & Cigna Commercial claims bill 95955, which denies 80.9% (PI-55/N623 on Aetna CPB 0697; CO-16/M76 on Cigna Policy 0509).',
        recommendedChange:
          "Add Dynamic Rule: (payer IN ('AETNA', 'CIGNA') OR carrier CONTAINS 'AETNA' OR carrier CONTAINS 'MERITAIN' OR carrier CONTAINS 'CIGNA' OR carrier CONTAINS 'ALLEGIANCE') -> use_95822_for_eeg.",
        deniedLinesEliminated: `${ac955Den} lines (${ac955Mn} Med-Nec PI-55 / CO-16)`,
        deniedDollarsEliminated: formatCurrency(ac955DenDollars),
        expectedImpact: `Lifts Aetna/Cigna EEG win rate from ${((ac955Paid / Math.max(aetCig955.length, 1)) * 100).toFixed(1)}% (${ac955Paid}/${aetCig955.length} on 95955) to ${(ac822WinRate * 100).toFixed(1)}% (95822 benchmark) -> Projected +${estNewPaidLinesAc} additional paid EEG lines (~+${formatCurrency(estNewCashAc)} net new cash).`,
      },
      {
        priority: '4. BCBS & Ambetter Commercial (Keep 95955)',
        currentState: 'CLEAN (No use_95822_for_eeg rule): Bills 95955 on non-cranial cases.',
        recommendedChange:
          'No change to EEG code selection (keep 95955 on BCBS & Ambetter); ensure NCCI XU modifier attaches to 95940/G0453 when paired with 95955.',
        deniedLinesEliminated: '— (Protected)',
        deniedDollarsEliminated: '— (Protected)',
        expectedImpact: `Protects ${bcbs955Paid}/${bcbs955.length} (${((bcbs955Paid / Math.max(bcbs955.length, 1)) * 100).toFixed(1)}% win rate = ${formatCurrency(bcbs955PaidDollars)} cash paid) where 95955 outperforms 95822 by +10.8%.`,
      },
    ];

    // Build 9-Scenario Weekly Small Multiples & Head-to-Head Matrix
    const scenarioCards = [];
    const scenMatrixRows = [];

    scenarioOrder.forEach((scenName, sIdx) => {
      const subAll = scopedEeg.filter((l) => l.eeg_scenario === scenName);
      const subWk = subAll.filter((l) => {
        const wk = dateMode === 'EOB_WEEK' ? l.eob_week || l.dos_week : l.dos_week;
        if (!wk || wk < '2026-07-27') return false;
        if (
          dateMode === 'DOS_WEEK' &&
          excludeImmatureWeeks &&
          dosMaturityInfo.matureDosWeeksSet &&
          !dosMaturityInfo.matureDosWeeksSet.has(wk)
        ) {
          return false;
        }
        if (dateMode === 'EOB_WEEK' && excludeImmatureWeeks && wk < '2026-08-31') {
          return false;
        }
        return true;
      });

      const byWk = {};
      subWk.forEach((l) => {
        const wk = dateMode === 'EOB_WEEK' ? l.eob_week || l.dos_week : l.dos_week;
        if (!byWk[wk]) {
          byWk[wk] = {
            week: wk,
            eobLines: 0,
            paidLines: 0,
            allDeniedLines: 0,
            medNecLines: 0,
            lines95822: 0,
            lines95955: 0,
          };
        }
        const slot = byWk[wk];
        slot.eobLines += 1;
        slot.paidLines += Number(l.is_paid || 0);
        slot.allDeniedLines += Number(l.is_denied || 0);
        slot.medNecLines += Number(l.is_med_nec_denied || 0);
        if (l.cpt_code === '95822') slot.lines95822 += 1;
        else if (l.cpt_code === '95955') slot.lines95955 += 1;
      });

      const weeklySeries = Object.values(byWk)
        .sort((a, b) => a.week.localeCompare(b.week))
        .map((w) => {
          const wd = Math.max(w.eobLines, 1);
          return {
            ...w,
            paidPct: Math.round((w.paidLines / wd) * 1000) / 10,
            allDeniedPct: Math.round((w.allDeniedLines / wd) * 1000) / 10,
            medNecPct: Math.round((w.medNecLines / wd) * 1000) / 10,
          };
        });

      const totWk = Math.max(subWk.length, 1);
      scenarioCards.push({
        idx: sIdx + 1,
        scenarioName: scenName,
        totalLines: subWk.length,
        paidPct: ((sumField(subWk, 'is_paid') / totWk) * 100).toFixed(1),
        medNecPct: ((sumField(subWk, 'is_med_nec_denied') / totWk) * 100).toFixed(1),
        weeklySeries,
        note: scenarioNotes[scenName] || '',
      });

      if (subAll.length > 0) {
        const s822 = subAll.filter((l) => l.cpt_code === '95822');
        const s955 = subAll.filter((l) => l.cpt_code === '95955');
        const l822 = s822.length;
        const p822 = sumField(s822, 'is_paid');
        const mn822 = sumField(s822, 'is_med_nec_denied');
        const compPaid822 = s822.filter((l) => l.is_denied && l.companion_paid_on_claim).length;

        const l955 = s955.length;
        const p955 = sumField(s955, 'is_paid');
        const mn955 = sumField(s955, 'is_med_nec_denied');

        const codeCounts = {};
        subAll.forEach((l) => {
          if (l.is_denied && l.display_code && l.display_code !== 'PAID') {
            codeCounts[l.display_code] = (codeCounts[l.display_code] || 0) + 1;
          }
        });
        const topCodes =
          Object.entries(codeCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([c]) => c)
            .join(', ') || '—';

        scenMatrixRows.push({
          scenarioName: scenName,
          l822,
          paid822Str: l822 ? `${((p822 / l822) * 100).toFixed(1)}% (${p822})` : '—',
          mn822Str: l822 ? `${((mn822 / l822) * 100).toFixed(1)}% (${mn822})` : '—',
          compPaid822: l822 ? compPaid822 : 0,
          l955,
          paid955Str: l955 ? `${((p955 / l955) * 100).toFixed(1)}% (${p955})` : '—',
          mn955Str: l955 ? `${((mn955 / l955) * 100).toFixed(1)}% (${mn955})` : '—',
          totalDeniedDollars: sumField(subAll, 'denied_amount'),
          topCodes,
          note: scenarioNotes[scenName] || '',
        });
      }
    });

    // Carrier x CPT Drill-Down Table filtered by selectedEegScenario
    const carrierGroups = {};
    scopedEeg.forEach((l) => {
      if (selectedEegScenario !== 'ALL_SCENARIOS' && l.eeg_scenario !== selectedEegScenario) return;
      const key = `${l.eeg_scenario}::${l.payer_name}::${l.cpt_code}`;
      if (!carrierGroups[key]) {
        carrierGroups[key] = {
          key,
          eeg_scenario: l.eeg_scenario,
          carrier_name: l.payer_name,
          cpt_code: l.cpt_code,
          eob_lines: 0,
          paid_lines: 0,
          all_denied: 0,
          med_nec_denied: 0,
          denied_on_paid_claim: 0,
          paid_dollars: 0,
          denied_dollars: 0,
          codeCounts: {},
        };
      }
      const g = carrierGroups[key];
      g.eob_lines += 1;
      g.paid_lines += Number(l.is_paid || 0);
      g.all_denied += Number(l.is_denied || 0);
      g.med_nec_denied += Number(l.is_med_nec_denied || 0);
      if (l.is_denied && l.companion_paid_on_claim) {
        g.denied_on_paid_claim += 1;
      }
      g.paid_dollars += Number(l.paid_amount || 0);
      g.denied_dollars += Number(l.denied_amount || 0);
      if (l.is_denied && l.display_code && l.display_code !== 'PAID') {
        g.codeCounts[l.display_code] = (g.codeCounts[l.display_code] || 0) + 1;
      }
    });

    const carrierBreakdownRows = Object.values(carrierGroups)
      .map((g) => {
        const d = Math.max(g.eob_lines, 1);
        const topCodes =
          Object.entries(g.codeCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([c]) => c)
            .join(', ') || '—';
        return {
          ...g,
          paid_pct: Math.round((g.paid_lines / d) * 1000) / 10,
          med_nec_pct: Math.round((g.med_nec_denied / d) * 1000) / 10,
          top_denial_codes: topCodes,
        };
      })
      .sort((a, b) => b.eob_lines - a.eob_lines || b.denied_dollars - a.denied_dollars);

    return {
      scenarioOrder,
      sqlMigration,
      dbRules,
      kpis,
      recImpactRows,
      scenarioCards,
      scenMatrixRows,
      carrierBreakdownRows,
    };
  }, [
    eegAnalysis,
    claimTypeScope,
    dateMode,
    excludeImmatureWeeks,
    dosMaturityInfo,
    startDate,
    endDate,
    selectedEegScenario,
  ]);

  // Overall EOB denominator summary across the active weekly trend scope
  const overallTrendSummary = useMemo(() => {
    let totAdj = 0;
    let totPaid = 0;
    let totAllDen = 0;
    let totMedNec = 0;
    weeklyTrendSeries.forEach((w) => {
      totAdj += w.eobAdjLines;
      totPaid += w.eobPaidLines;
      totAllDen += w.allDeniedLines;
      totMedNec += w.medNecLines;
    });
    const safeD = Math.max(totAdj, 1);
    return {
      totAdj,
      totPaid,
      totAllDen,
      totMedNec,
      medNecPctOfEob: totAdj > 0 ? Math.round((totMedNec / safeD) * 1000) / 10 : 0,
      allDenPctOfEob: totAdj > 0 ? Math.round((totAllDen / safeD) * 1000) / 10 : 0,
      paidPctOfEob: totAdj > 0 ? Math.round((totPaid / safeD) * 1000) / 10 : 0,
    };
  }, [weeklyTrendSeries]);

  // Breakdown Matrix 1: Payer x CPT x Denial Reason Pattern Matrix
  const patternMatrixRows = useMemo(() => {
    const groups = {};
    filteredLines.forEach((l) => {
      const key = `${l.payer_name}::${l.cpt_code}::${l.subtype_key}::${l.display_code}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          payer_name: l.payer_name,
          cpt_code: l.cpt_code,
          subtype_key: l.subtype_key,
          subtype_short_label: l.subtype_short_label,
          subtype_badge_color: l.subtype_badge_color,
          signal_source: l.signal_source,
          signal_source_label: l.signal_source_label,
          signal_source_color: l.signal_source_color,
          display_code: l.display_code,
          sample_description: l.description || '',
          denied_lines: 0,
          claimsSet: new Set(),
          denied_amount: 0,
          ptrACounts: {},
          buriedCount: 0,
        };
      }
      const g = groups[key];
      g.denied_lines += 1;
      g.claimsSet.add(l.claim_number);
      g.denied_amount += Number(l.denied_amount || 0);
      if (!g.sample_description && l.description) {
        g.sample_description = l.description;
      }
      if (l.pointer_a_code && l.pointer_a_code !== 'NONE') {
        const pKey = `${l.pointer_a_code}|${l.pointer_a_tier_label}|${l.pointer_a_badge_color}`;
        g.ptrACounts[pKey] = (g.ptrACounts[pKey] || 0) + 1;
      }
      if (l.has_buried_high_acuity) {
        g.buriedCount += 1;
      }
    });

    return Object.values(groups)
      .map((g) => {
        const topPtrAEntry = Object.entries(g.ptrACounts).sort((a, b) => b[1] - a[1])[0];
        let topPtrACode = 'N/A';
        let topPtrATier = '';
        let topPtrAColor = 'gray';
        if (topPtrAEntry) {
          const [codePart, tierPart, colorPart] = topPtrAEntry[0].split('|');
          topPtrACode = codePart;
          topPtrATier = tierPart;
          topPtrAColor = colorPart || 'gray';
        }
        return {
          ...g,
          claims_count: g.claimsSet.size,
          top_pointer_a_code: topPtrACode,
          top_pointer_a_tier: topPtrATier,
          top_pointer_a_color: topPtrAColor,
        };
      })
      .sort((a, b) => b.denied_lines - a.denied_lines || b.denied_amount - a.denied_amount)
      .slice(0, 35);
  }, [filteredLines]);

  // Breakdown Matrix 2A: By CPT Modality
  const cptBreakdownRows = useMemo(() => {
    // Aggregate EOB adjudication baseline per CPT for the selected claimTypeScope
    const adjByCpt = {};
    cptAdjTotals.forEach((row) => {
      if (claimTypeScope !== 'all' && row.claim_type !== claimTypeScope) return;
      if (exclude95999 && row.cpt_code === '95999') return;
      const c = row.cpt_code;
      if (!adjByCpt[c]) {
        adjByCpt[c] = {
          cpt_code: c,
          eob_adj_lines: 0,
          eob_paid_or_allowed_lines: 0,
          eob_all_denied_lines: 0,
        };
      }
      adjByCpt[c].eob_adj_lines += Number(row.eob_adj_lines || 0);
      adjByCpt[c].eob_paid_or_allowed_lines += Number(row.eob_paid_or_allowed_lines || 0);
      adjByCpt[c].eob_all_denied_lines += Number(row.eob_all_denied_lines || 0);
    });

    // Overlay filtered Medical Necessity lines per CPT
    const medNecByCpt = {};
    filteredLines.forEach((l) => {
      const c = l.cpt_code;
      if (!medNecByCpt[c]) {
        medNecByCpt[c] = {
          med_nec_lines: 0,
          claimsSet: new Set(),
          denied_amount: 0,
          codeCounts: {},
        };
      }
      medNecByCpt[c].med_nec_lines += 1;
      medNecByCpt[c].claimsSet.add(l.claim_number);
      medNecByCpt[c].denied_amount += Number(l.denied_amount || 0);
      if (l.display_code) {
        medNecByCpt[c].codeCounts[l.display_code] = (medNecByCpt[c].codeCounts[l.display_code] || 0) + 1;
      }
    });

    return Object.keys(medNecByCpt)
      .map((c) => {
        const base = adjByCpt[c] || { eob_adj_lines: medNecByCpt[c].med_nec_lines, eob_paid_or_allowed_lines: 0 };
        const mn = medNecByCpt[c];
        const denom = Math.max(base.eob_adj_lines, 1);
        const topCode = Object.entries(mn.codeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
        return {
          cpt_code: c,
          eob_adj_lines: base.eob_adj_lines,
          paid_rate_pct: Math.round((base.eob_paid_or_allowed_lines / denom) * 1000) / 10,
          med_nec_lines: mn.med_nec_lines,
          med_nec_claims: mn.claimsSet.size,
          med_nec_rate_pct: Math.round((mn.med_nec_lines / denom) * 1000) / 10,
          denied_amount: mn.denied_amount,
          top_display_code: topCode,
        };
      })
      .sort((a, b) => b.med_nec_lines - a.med_nec_lines || b.denied_amount - a.denied_amount);
  }, [cptAdjTotals, filteredLines, claimTypeScope, exclude95999]);

  // Breakdown Matrix 2B: By Billed Pointer A Acuity Tier
  const tierBreakdownRows = useMemo(() => {
    const tierOrder = [
      'TIER_1',
      'TIER_2',
      'TIER_3',
      'TIER_4A',
      'TIER_4B',
      'TIER_4C',
      'TIER_4D',
      'TIER_4E',
      'TIER_5',
      'NONE',
    ];
    const baseByTier = {};
    pointerATierTotals.forEach((row) => {
      if (claimTypeScope !== 'all' && row.claim_type !== claimTypeScope) return;
      const tk = row.tier_key;
      if (!baseByTier[tk]) {
        baseByTier[tk] = {
          tier_key: tk,
          short_label: row.short_label,
          badge_color: row.badge_color || 'gray',
          eob_adj_claims: 0,
          eob_paid_claims: 0,
          eob_adj_lines: 0,
        };
      }
      baseByTier[tk].eob_adj_claims += Number(row.eob_adj_claims || 0);
      baseByTier[tk].eob_paid_claims += Number(row.eob_paid_claims || 0);
      baseByTier[tk].eob_adj_lines += Number(
        exclude95999 ? row.eob_adj_lines_core || 0 : row.eob_adj_lines_all || 0
      );
    });

    const mnByTier = {};
    filteredLines.forEach((l) => {
      const tk = l.pointer_a_tier_key || 'NONE';
      if (!mnByTier[tk]) {
        mnByTier[tk] = {
          short_label: l.pointer_a_tier_label || tk,
          badge_color: l.pointer_a_badge_color || 'gray',
          med_nec_lines: 0,
          claimsSet: new Set(),
          denied_amount: 0,
          buried_lines: 0,
        };
      }
      mnByTier[tk].med_nec_lines += 1;
      mnByTier[tk].claimsSet.add(l.claim_number);
      mnByTier[tk].denied_amount += Number(l.denied_amount || 0);
      if (l.has_buried_high_acuity) {
        mnByTier[tk].buried_lines += 1;
      }
    });

    const allKeys = Array.from(new Set([...Object.keys(baseByTier), ...Object.keys(mnByTier)]));
    return allKeys
      .map((tk) => {
        const b = baseByTier[tk] || {
          tier_key: tk,
          short_label: mnByTier[tk]?.short_label || tk,
          badge_color: mnByTier[tk]?.badge_color || 'gray',
          eob_adj_claims: 0,
          eob_paid_claims: 0,
          eob_adj_lines: 0,
        };
        const m = mnByTier[tk] || {
          med_nec_lines: 0,
          claimsSet: new Set(),
          denied_amount: 0,
          buried_lines: 0,
        };
        const adjC = Math.max(b.eob_adj_claims, 1);
        const adjL = Math.max(b.eob_adj_lines, 1);
        return {
          tier_key: tk,
          short_label: b.short_label,
          badge_color: b.badge_color,
          eob_adj_claims: b.eob_adj_claims,
          claim_win_rate_pct: b.eob_adj_claims
            ? Math.round((b.eob_paid_claims / adjC) * 1000) / 10
            : 0,
          eob_adj_lines: b.eob_adj_lines,
          med_nec_lines: m.med_nec_lines,
          med_nec_claims: m.claimsSet.size,
          med_nec_line_rate_pct: b.eob_adj_lines
            ? Math.round((m.med_nec_lines / adjL) * 1000) / 10
            : 0,
          denied_amount: m.denied_amount,
          buried_lines: m.buried_lines,
        };
      })
      .filter((r) => r.eob_adj_claims > 0 || r.med_nec_lines > 0)
      .sort((a, b) => {
        const idxA = tierOrder.indexOf(a.tier_key);
        const idxB = tierOrder.indexOf(b.tier_key);
        return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
      });
  }, [pointerATierTotals, filteredLines, claimTypeScope, exclude95999]);

  // Paginated Line-Level Explorer rows
  const totalPages = Math.max(1, Math.ceil(filteredLines.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedLines = useMemo(() => {
    const startIdx = (safePage - 1) * pageSize;
    return filteredLines.slice(startIdx, startIdx + pageSize);
  }, [filteredLines, safePage, pageSize]);

  const hasActiveSubFilters = Boolean(
    subtypeFilter !== 'ALL' ||
      signalSourceFilter !== 'ALL' ||
      diagnosticFilter !== 'ALL' ||
      payerGroupFilter !== 'ALL' ||
      payerFilter !== 'ALL' ||
      cptFilter !== 'ALL' ||
      tierFilter !== 'ALL' ||
      displayCodeFilter !== 'ALL' ||
      startDate ||
      endDate ||
      dateMode !== 'EOB_WEEK' ||
      !excludeImmatureWeeks ||
      searchQuery.trim()
  );

  return (
    <Box>
      {/* Top Scope & Filter Bar */}
      <Card variant="outline" mb={4} bg="gray.50" borderColor="gray.200">
        <CardBody py={3} px={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <HStack spacing={2} mb={0.5}>
                <Badge colorScheme="red" variant="solid">
                  STAGE 3: EOB LINE-LEVEL MEDICAL NECESSITY
                </Badge>
                <Badge colorScheme="purple" variant="subtle">
                  Explicit CARCs + RARC-Implied (CO-16/96) + No-CARC Footnotes/Text
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.600">
                Isolates EOB service line denials driven by medical necessity, principal diagnosis (<Code fontSize="10px">Pointer A</Code>), CPT × ICD-10 crosswalks, LCD/NCDs, experimental/unproven policies, and post-review clinical documentation.
              </Text>
            </Box>

            <HStack spacing={2} wrap="wrap">
              {/* Claim Type Toggle */}
              <HStack spacing={1} bg="white" p={1} borderRadius="md" border="1px solid" borderColor="gray.200">
                {[
                  { id: 'pro', label: 'Pro (P*) Only' },
                  { id: 'all', label: 'All (Pro + Tech)' },
                  { id: 'tech', label: 'Tech (T*) Only' },
                ].map((btn) => (
                  <Button
                    key={btn.id}
                    size="xs"
                    colorScheme={claimTypeScope === btn.id ? 'blue' : 'gray'}
                    variant={claimTypeScope === btn.id ? 'solid' : 'ghost'}
                    onClick={() => setClaimTypeScope(btn.id)}
                  >
                    {btn.label}
                  </Button>
                ))}
              </HStack>

              {/* Exclude 95999 Toggle */}
              <HStack spacing={1} bg="white" p={1} borderRadius="md" border="1px solid" borderColor="gray.200">
                <Button
                  size="xs"
                  colorScheme={exclude95999 ? 'teal' : 'gray'}
                  variant={exclude95999 ? 'solid' : 'ghost'}
                  onClick={() => setExclude95999(true)}
                >
                  Exclude 95999 (Core IONM)
                </Button>
                <Button
                  size="xs"
                  colorScheme={!exclude95999 ? 'orange' : 'gray'}
                  variant={!exclude95999 ? 'solid' : 'ghost'}
                  onClick={() => setExclude95999(false)}
                >
                  Include 95999
                </Button>
              </HStack>

              {/* Date Axis Toggle (Default = EOB Remittance Week vs DOS Surgery Week) */}
              <HStack spacing={1} bg="white" p={1} borderRadius="md" border="1px solid" borderColor="gray.200">
                <Button
                  size="xs"
                  colorScheme={dateMode === 'EOB_WEEK' ? 'purple' : 'gray'}
                  variant={dateMode === 'EOB_WEEK' ? 'solid' : 'ghost'}
                  onClick={() => setDateMode('EOB_WEEK')}
                >
                  By EOB Week (Remittance)
                </Button>
                <Button
                  size="xs"
                  colorScheme={dateMode === 'DOS_WEEK' ? 'purple' : 'gray'}
                  variant={dateMode === 'DOS_WEEK' ? 'solid' : 'ghost'}
                  onClick={() => setDateMode('DOS_WEEK')}
                >
                  By DOS Week (Surgery)
                </Button>
              </HStack>

              {/* Exclude Immature Cohorts Toggle */}
              <ChakraTooltip
                label={
                  dateMode === 'DOS_WEEK'
                    ? 'Excludes recent surgery weeks (<25% EOB completion) where fast automated denials (e.g., Cigna Policy 0509) arrive before 14-day payment floor EFT runs.'
                    : 'Excludes initial pre-steady-state August backfill EOB weeks (<200 EOB lines) so weekly remittance trends start at full volume.'
                }
                hasArrow
              >
                <Button
                  size="xs"
                  colorScheme={excludeImmatureWeeks ? 'green' : 'gray'}
                  variant={excludeImmatureWeeks ? 'solid' : 'outline'}
                  bg={excludeImmatureWeeks ? undefined : 'white'}
                  onClick={() => setExcludeImmatureWeeks((v) => !v)}
                >
                  {dateMode === 'DOS_WEEK'
                    ? `Mature DOS Weeks (≥25% w/ EOB): ${excludeImmatureWeeks ? 'ON' : 'OFF'}`
                    : `Steady-State EOB Weeks: ${excludeImmatureWeeks ? 'ON' : 'OFF'}`}
                </Button>
              </ChakraTooltip>

              {(hasActiveSubFilters || claimTypeScope !== 'pro' || !exclude95999) && (
                <Button
                  size="xs"
                  leftIcon={<RepeatIcon />}
                  colorScheme="gray"
                  variant="outline"
                  bg="white"
                  onClick={handleResetFilters}
                >
                  Reset View
                </Button>
              )}
            </HStack>
          </Flex>

          {/* Secondary Filter Controls (Search, Payer Group, Specific Carrier, CPT, Date Range, Signal Source, Diagnostic) */}
          <Flex mt={3} pt={2.5} borderTop="1px solid" borderColor="gray.200" gap={2} wrap="wrap" align="center">
            <Input
              size="xs"
              bg="white"
              w="195px"
              placeholder="Search Claim #, Payer, CPT, CARC, DX..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Select
              size="xs"
              bg="white"
              w="180px"
              value={payerGroupFilter}
              onChange={(e) => {
                setPayerGroupFilter(e.target.value);
                setPayerFilter('ALL');
              }}
            >
              <option value="ALL">All Payer Groups ({filterOptions.payerGroups.length})</option>
              {filterOptions.payerGroups.map((pg) => (
                <option key={pg.name} value={pg.name}>
                  {pg.name} ({pg.cnt})
                </option>
              ))}
            </Select>
            <Select
              size="xs"
              bg="white"
              w="195px"
              value={payerFilter}
              onChange={(e) => setPayerFilter(e.target.value)}
            >
              <option value="ALL">All Specific Carriers ({filterOptions.payers.length})</option>
              {filterOptions.payers.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} ({p.cnt})
                </option>
              ))}
            </Select>
            <Select
              size="xs"
              bg="white"
              w="140px"
              value={cptFilter}
              onChange={(e) => setCptFilter(e.target.value)}
            >
              <option value="ALL">All CPTs ({filterOptions.cpts.length})</option>
              {filterOptions.cpts.map((c) => (
                <option key={c.code} value={c.code}>
                  CPT {c.code} ({c.cnt})
                </option>
              ))}
            </Select>
            <HStack spacing={1} bg="white" px={2} py={0.5} borderRadius="md" border="1px solid" borderColor="gray.200">
              <Text fontSize="10px" fontWeight="bold" color="gray.600">
                {dateMode === 'EOB_WEEK' ? 'EOB Date:' : 'DOS:'}
              </Text>
              <Input
                type="date"
                size="xs"
                w="115px"
                border="none"
                p={0}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Text fontSize="10px" color="gray.400">
                to
              </Text>
              <Input
                type="date"
                size="xs"
                w="115px"
                border="none"
                p={0}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </HStack>
            <Select
              size="xs"
              bg="white"
              w="195px"
              value={signalSourceFilter}
              onChange={(e) => setSignalSourceFilter(e.target.value)}
            >
              <option value="ALL">All Signal Sources (CARC / RARC / Text)</option>
              {signalSources.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.short_label}
                </option>
              ))}
            </Select>
            <Select
              size="xs"
              bg="white"
              w="205px"
              value={diagnosticFilter}
              onChange={(e) => setDiagnosticFilter(e.target.value)}
            >
              <option value="ALL">All Root-Cause Diagnostics</option>
              {diagnostics.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </Select>
            {displayCodeFilter !== 'ALL' && (
              <Badge
                colorScheme="purple"
                px={2}
                py={1}
                borderRadius="md"
                cursor="pointer"
                onClick={() => setDisplayCodeFilter('ALL')}
              >
                Code Filter: {displayCodeFilter} ✕
              </Badge>
            )}
            {tierFilter !== 'ALL' && (
              <Badge
                colorScheme="blue"
                px={2}
                py={1}
                borderRadius="md"
                cursor="pointer"
                onClick={() => setTierFilter('ALL')}
              >
                Pointer A Tier: {tierFilter} ✕
              </Badge>
            )}
          </Flex>
        </CardBody>
      </Card>

      {/* 6 Clickable Medical Necessity Subtype Cards */}
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Heading size="xs" color="gray.700" textTransform="uppercase">
            EOB Medical Necessity Subtypes (Click Card to Filter)
          </Heading>
          {subtypeFilter !== 'ALL' && (
            <Button size="xs" variant="link" colorScheme="blue" onClick={() => setSubtypeFilter('ALL')}>
              Show All Subtypes ({subtypeStats.totalLines.toLocaleString()} lines)
            </Button>
          )}
        </HStack>
        <Text fontSize="xs" color="gray.600">
          Active Scope Total: <b>{filteredLines.length.toLocaleString()} Denied EOB Lines</b> across{' '}
          <b>{new Set(filteredLines.map((l) => l.claim_number)).size.toLocaleString()} Claims</b> (
          <Text as="span" color="red.600" fontWeight="bold">
            {formatCurrency(filteredLines.reduce((acc, l) => acc + Number(l.denied_amount || 0), 0))} Denied (beta)
          </Text>
          )
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 6 }} spacing={3} mb={4}>
        {subtypeStats.cards.map((st) => {
          const isSelected = subtypeFilter === st.subtype_key;
          return (
            <ChakraTooltip key={st.subtype_key} label={st.description} hasArrow placement="top">
              <Card
                variant="outline"
                cursor="pointer"
                onClick={() => setSubtypeFilter(isSelected ? 'ALL' : st.subtype_key)}
                borderTop="3px solid"
                borderTopColor={`${st.badge_color}.500`}
                bg={isSelected ? `${st.badge_color}.50` : 'white'}
                borderColor={isSelected ? `${st.badge_color}.400` : 'gray.200'}
                shadow={isSelected ? 'md' : 'xs'}
                _hover={{ borderColor: `${st.badge_color}.400`, shadow: 'sm' }}
                transition="all 0.15s"
              >
                <CardBody py={2.5} px={3}>
                  <Flex justify="space-between" align="center" mb={0.5}>
                    <Badge colorScheme={st.badge_color} fontSize="9px">
                      {st.short_label}
                    </Badge>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500">
                      {st.sharePct}%
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="extrabold" color="gray.800" my={0.5}>
                    {st.lines.toLocaleString()}{' '}
                    <Text as="span" fontSize="xs" fontWeight="semibold" color="gray.500">
                      lines ({st.claims}c)
                    </Text>
                  </Text>
                  <Text fontSize="11px" color="red.600" fontWeight="bold">
                    {formatCurrency(st.deniedAmount)} (beta)
                  </Text>
                  <Text fontSize="10px" color="gray.500" noOfLines={1} mt={0.5} title={st.code_examples}>
                    {st.code_examples}
                  </Text>
                </CardBody>
              </Card>
            </ChakraTooltip>
          );
        })}
      </SimpleGrid>

      {/* Sub-Tab Navigation Bar */}
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={2}
        mb={4}
        p={2}
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        shadow="xs"
      >
        <HStack spacing={2} wrap="wrap">
          {[
            {
              id: 'OVERVIEW',
              label: '📈 1. Overview, 4 Weekly Trend Charts & Line Explorer',
              color: 'blue',
            },
            {
              id: 'SMALL_MULTIPLES',
              label: '🏢 2. Payer Group & Top 30 Carrier Small Multiples',
              color: 'purple',
            },
            {
              id: 'EEG_SCENARIOS',
              label: '⚡ 3. Intraoperative EEG (95822 vs. 95955) & Rule Scenarios',
              color: 'orange',
            },
          ].map((tabBtn) => (
            <Button
              key={tabBtn.id}
              size="sm"
              colorScheme={activeSubTab === tabBtn.id ? tabBtn.color : 'gray'}
              variant={activeSubTab === tabBtn.id ? 'solid' : 'ghost'}
              onClick={() => setActiveSubTab(tabBtn.id)}
            >
              {tabBtn.label}
            </Button>
          ))}
        </HStack>

        <Text fontSize="xs" color="gray.600">
          Time Axis: <b>{dateMode === 'EOB_WEEK' ? 'By EOB Remittance Week (Standard)' : 'By Surgery DOS Week'}</b>
        </Text>
      </Flex>

      {/* Mature DOS Cohort Filter Info Banner when DOS_WEEK is active */}
      {dateMode === 'DOS_WEEK' &&
        excludeImmatureWeeks &&
        dosMaturityInfo.excludedDosWeeksInfo.length > 0 && (
          <Alert status="info" variant="subtle" borderRadius="md" py={2} px={3} mb={4} fontSize="xs">
            <AlertIcon boxSize="14px" />
            <Box>
              <b>Mature DOS Cohorts Filter Active (≥25% EOBs Received):</b> Hiding immature recent surgery weeks (
              <Code fontSize="10px">{dosMaturityInfo.excludedDosWeeksInfo.join(', ')}</Code>) where &gt;75% of claims
              are still awaiting their first EOB. Toggle <b>Mature DOS Weeks: OFF</b> above to view raw tail weeks.
            </Box>
          </Alert>
        )}

      {/* ===================================================================== */}
      {/* SUB-TAB 1: OVERVIEW, 4 WEEKLY TREND CHARTS & LINE-LEVEL EXPLORER      */}
      {/* ===================================================================== */}
      {activeSubTab === 'OVERVIEW' && (
        <>
          {/* 4 Synchronized 2-Panel Weekly Trend Graphs (2x2 Grid) */}
          {weeklyTrendSeries.length > 0 && (
            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mb={5}>
              {/* Graph 1: Weekly EOB Medical Necessity Denial Rate % vs. All EOB Outcomes */}
              <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
                <CardBody p={3.5}>
                  <Box mb={2}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.900">
                      1. Weekly EOB Medical Necessity Denial Rate % (By{' '}
                      {dateMode === 'EOB_WEEK' ? 'EOB Remittance Week' : 'Surgery DOS Week'})
                    </Text>
                    <HStack spacing={2} mt={0.5} wrap="wrap" fontSize="11px">
                      <Text color="gray.700">
                        Total EOB Lines: <b>{overallTrendSummary.totAdj.toLocaleString()}</b>
                      </Text>
                      <Text color="gray.400">|</Text>
                      <Text color="red.600" fontWeight="bold">
                        ● Med-Nec Denied: {overallTrendSummary.medNecPctOfEob}% (
                        {overallTrendSummary.totMedNec.toLocaleString()})
                      </Text>
                      <Text color="gray.400">|</Text>
                      <Text color="orange.600" fontWeight="semibold">
                        ● All EOB Denied: {overallTrendSummary.allDenPctOfEob}% (
                        {overallTrendSummary.totAllDen.toLocaleString()})
                      </Text>
                      <Text color="gray.400">|</Text>
                      <Text color="green.600" fontWeight="semibold">
                        ● EOB Paid/Allowed: {overallTrendSummary.paidPctOfEob}% (
                        {overallTrendSummary.totPaid.toLocaleString()})
                      </Text>
                    </HStack>
                  </Box>

                  {/* Top Panel: % Rate Lines (0 - 100%) */}
                  <Box h="165px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 8, right: 12, left: -14, bottom: 2 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="week" hide />
                        <YAxis
                          domain={[0, 100]}
                          ticks={[0, 25, 50, 75, 100]}
                          tick={{ fontSize: 10, fill: '#1A202C' }}
                          unit="%"
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '6px 10px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
                          formatter={(val, name, props) => {
                            const p = props?.payload || {};
                            const denom = p.denom || 0;
                            let num = 0;
                            if (name === 'Med-Nec Denied %') num = p.medNecLines || 0;
                            else if (name === 'All EOB Denied %') num = p.allDeniedLines || 0;
                            else if (name === 'EOB Paid/Allowed %') num = p.eobPaidLines || 0;
                            return [
                              val !== null && val !== undefined
                                ? `${val}% (${num.toLocaleString()} / ${denom.toLocaleString()} EOB lines)`
                                : 'No volume',
                              name,
                            ];
                          }}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '2px' }} />
                        <Line
                          type="monotone"
                          dataKey="medNecPct"
                          name="Med-Nec Denied %"
                          stroke="#DC2626"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#DC2626' }}
                          activeDot={{ r: 5 }}
                          connectNulls
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="allDeniedPct"
                          name="All EOB Denied %"
                          stroke="#EA580C"
                          strokeWidth={1.8}
                          strokeDasharray="4 3"
                          dot={{ r: 2.5, fill: '#EA580C' }}
                          connectNulls
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="paidPct"
                          name="EOB Paid/Allowed %"
                          stroke="#16A34A"
                          strokeWidth={2.0}
                          dot={{ r: 2.5, fill: '#16A34A' }}
                          connectNulls
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Bottom Panel: Weekly EOB-Adjudicated Line Volume Bars */}
                  <Box h="95px" w="100%" mt={1}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 2, right: 12, left: -14, bottom: 18 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 9, fill: '#1A202C' }}
                          angle={-30}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '5px 8px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                          formatter={(val) => [`${Number(val || 0).toLocaleString()} EOB lines`, 'Total EOB-Adjudicated Lines']}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        <Bar
                          dataKey="eobAdjLines"
                          name="Total EOB-Adjudicated Lines"
                          fill="#94A3B8"
                          stroke="#64748B"
                          strokeWidth={0.8}
                          isAnimationActive={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              {/* Graph 2: Weekly Medical Necessity Denials by Clinical Subtype (% Rate + Stacked Volume) */}
              <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
                <CardBody p={3.5}>
                  <Box mb={2}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.900">
                      2. Weekly Medical Necessity Denials by Clinical Subtype (By{' '}
                      {dateMode === 'EOB_WEEK' ? 'EOB Remittance Week' : 'Surgery DOS Week'})
                    </Text>
                    <Text fontSize="11px" color="gray.600">
                      Top: Subtype Denial Rate (% of all EOB-adjudicated lines) | Bottom: Stacked Med-Nec Denied Line Volume by Subtype
                    </Text>
                  </Box>

                  {/* Top Panel: Subtype % of EOB-Adjudicated Lines */}
                  <Box h="165px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 8, right: 12, left: -14, bottom: 2 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="week" hide />
                        <YAxis tick={{ fontSize: 10, fill: '#1A202C' }} unit="%" />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '6px 10px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
                          formatter={(val, name, props) => {
                            const p = props?.payload || {};
                            const matched = subtypes.find((st) => st.short_label === name);
                            const num = matched ? p[matched.subtype_key] || 0 : 0;
                            return [
                              val !== null && val !== undefined
                                ? `${val}% (${num.toLocaleString()} / ${(p.denom || 0).toLocaleString()} EOB lines)`
                                : '0%',
                              name,
                            ];
                          }}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        <Legend wrapperStyle={{ fontSize: '9px', paddingTop: '2px' }} />
                        {subtypes.map((st) => (
                          <Line
                            key={st.subtype_key}
                            type="monotone"
                            dataKey={`${st.subtype_key}_pct`}
                            name={st.short_label}
                            stroke={SUBTYPE_CHART_COLORS[st.subtype_key] || '#4A5568'}
                            strokeWidth={2.0}
                            dot={{ r: 2.5 }}
                            connectNulls
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Bottom Panel: Stacked Med-Nec Denied Lines by Subtype */}
                  <Box h="95px" w="100%" mt={1}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 2, right: 12, left: -14, bottom: 18 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 9, fill: '#1A202C' }}
                          angle={-30}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '5px 8px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                          formatter={(val, name) => [`${Number(val || 0).toLocaleString()} denied lines`, name]}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        {subtypes.map((st) => (
                          <Bar
                            key={st.subtype_key}
                            dataKey={st.subtype_key}
                            name={st.short_label}
                            stackId="subtypes"
                            fill={SUBTYPE_CHART_COLORS[st.subtype_key] || '#4A5568'}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              {/* Graph 3: Weekly Medical Necessity Denials by Signal Source (CARC vs. RARC vs. Text) */}
              <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
                <CardBody p={3.5}>
                  <Box mb={2}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.900">
                      3. Weekly Med-Nec Denials by Signal Source (CARC vs. RARC vs. Footnote/Text)
                    </Text>
                    <Text fontSize="11px" color="gray.600">
                      Top: Signal Source Rate (% of all EOB lines) | Bottom: Stacked Denied Line Volume by Signal Source
                    </Text>
                  </Box>

                  <Box h="165px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 8, right: 12, left: -14, bottom: 2 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="week" hide />
                        <YAxis tick={{ fontSize: 10, fill: '#1A202C' }} unit="%" />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '6px 10px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
                          formatter={(val, name, props) => {
                            const p = props?.payload || {};
                            const matched = signalSources.find((s) => s.short_label === name);
                            const num = matched ? p[matched.key] || 0 : 0;
                            return [
                              val !== null && val !== undefined
                                ? `${val}% (${num.toLocaleString()} / ${(p.denom || 0).toLocaleString()} EOB lines)`
                                : '0%',
                              name,
                            ];
                          }}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        <Legend wrapperStyle={{ fontSize: '9px', paddingTop: '2px' }} />
                        {signalSources.map((s) => (
                          <Line
                            key={s.key}
                            type="monotone"
                            dataKey={`${s.key}_pct`}
                            name={s.short_label}
                            stroke={SIGNAL_CHART_COLORS[s.key] || '#4A5568'}
                            strokeWidth={2.1}
                            dot={{ r: 2.5 }}
                            connectNulls
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box h="95px" w="100%" mt={1}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 2, right: 12, left: -14, bottom: 18 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 9, fill: '#1A202C' }}
                          angle={-30}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '5px 8px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                          formatter={(val, name) => [`${Number(val || 0).toLocaleString()} denied lines`, name]}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        {signalSources.map((s) => (
                          <Bar
                            key={s.key}
                            dataKey={s.key}
                            name={s.short_label}
                            stackId="signals"
                            fill={SIGNAL_CHART_COLORS[s.key] || '#4A5568'}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              {/* Graph 4: Weekly Medical Necessity Denials by Root-Cause Diagnostic */}
              <Card variant="outline" bg="white" borderColor="gray.200" shadow="xs">
                <CardBody p={3.5}>
                  <Box mb={2}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.900">
                      4. Weekly Med-Nec Denials by Root-Cause Diagnostic
                    </Text>
                    <Text fontSize="11px" color="gray.600">
                      Top: Root-Cause Rate (% of all EOB lines) | Bottom: Stacked Denied Line Volume by Root-Cause Diagnostic
                    </Text>
                  </Box>

                  <Box h="165px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 8, right: 12, left: -14, bottom: 2 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="week" hide />
                        <YAxis tick={{ fontSize: 10, fill: '#1A202C' }} unit="%" />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '6px 10px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold', marginBottom: '2px' }}
                          formatter={(val, name, props) => {
                            const p = props?.payload || {};
                            const matched = diagnostics.find((d) => d.short_label === name);
                            const num = matched ? p[matched.key] || 0 : 0;
                            return [
                              val !== null && val !== undefined
                                ? `${val}% (${num.toLocaleString()} / ${(p.denom || 0).toLocaleString()} EOB lines)`
                                : '0%',
                              name,
                            ];
                          }}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        <Legend wrapperStyle={{ fontSize: '9px', paddingTop: '2px' }} />
                        {diagnostics.map((d) => (
                          <Line
                            key={d.key}
                            type="monotone"
                            dataKey={`${d.key}_pct`}
                            name={d.short_label}
                            stroke={DIAGNOSTIC_CHART_COLORS[d.key] || '#4A5568'}
                            strokeWidth={2.0}
                            dot={{ r: 2.5 }}
                            connectNulls
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box h="95px" w="100%" mt={1}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={weeklyTrendSeries}
                        syncId="med_nec_wk_sync"
                        margin={{ top: 2, right: 12, left: -14, bottom: 18 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 9, fill: '#1A202C' }}
                          angle={-30}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#1A202C' }} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1A202C',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '11px',
                            padding: '5px 8px',
                          }}
                          labelStyle={{ color: '#E2E8F0', fontWeight: 'bold' }}
                          formatter={(val, name) => [`${Number(val || 0).toLocaleString()} denied lines`, name]}
                          labelFormatter={(wk) => `Week of ${wk}`}
                        />
                        {diagnostics.map((d) => (
                          <Bar
                            key={d.key}
                            dataKey={d.key}
                            name={d.short_label}
                            stackId="diagnostics"
                            fill={DIAGNOSTIC_CHART_COLORS[d.key] || '#4A5568'}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>
            </SimpleGrid>
          )}

      {/* Signal Source & Root-Cause Diagnostic Interactive Strip */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} mb={5}>
        <Card variant="outline" size="sm">
          <CardBody py={2.5} px={3.5}>
            <Text fontSize="11px" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={2}>
              How We Flagged the EOB Denial (Signal Source — Click to Filter)
            </Text>
            <HStack spacing={2} wrap="wrap">
              {signalAndDiagnosticStats.signals.map((sig) => {
                const active = signalSourceFilter === sig.key;
                return (
                  <Button
                    key={sig.key}
                    size="xs"
                    colorScheme={sig.badge_color}
                    variant={active ? 'solid' : 'outline'}
                    onClick={() => setSignalSourceFilter(active ? 'ALL' : sig.key)}
                  >
                    {sig.short_label}: {sig.lines.toLocaleString()} ({sig.sharePct}%)
                  </Button>
                );
              })}
            </HStack>
          </CardBody>
        </Card>

        <Card variant="outline" size="sm">
          <CardBody py={2.5} px={3.5}>
            <Text fontSize="11px" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={2}>
              Automated Medical Necessity Diagnostic (Why It Denied — Click to Filter)
            </Text>
            <HStack spacing={1.5} wrap="wrap">
              {signalAndDiagnosticStats.diagnostics.map((diag) => {
                const active = diagnosticFilter === diag.key;
                return (
                  <Button
                    key={diag.key}
                    size="xs"
                    colorScheme={diag.badge_color}
                    variant={active ? 'solid' : 'outline'}
                    onClick={() => setDiagnosticFilter(active ? 'ALL' : diag.key)}
                  >
                    {diag.short_label}: {diag.lines.toLocaleString()} ({diag.sharePct}%)
                  </Button>
                );
              })}
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Table 1: Payer x CPT x Denial Reason Pattern Matrix */}
      <Card variant="outline" mb={5}>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Box>
              <Heading size="sm" color="gray.800">
                Payer × CPT × EOB Medical Necessity Reason Matrix (Top {patternMatrixRows.length} Patterns)
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Groups EOB line denials by Payer, CPT, Subtype, and CARC/RARC or CARC-less Footnote/Text reason. Click any row to filter the Line-Level Explorer below.
              </Text>
            </Box>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <TableContainer maxH="380px" overflowY="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th>Payer / Carrier</Th>
                  <Th>CPT</Th>
                  <Th>Med-Nec Subtype</Th>
                  <Th>Signal Source</Th>
                  <Th>CARC / RARC / Implied Code</Th>
                  <Th>Resolved EOB Reason / Footnote Text</Th>
                  <Th>Top Pointer A DX</Th>
                  <Th isNumeric>Denied Lines (Claims)</Th>
                  <Th isNumeric>Denied $ (beta)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {patternMatrixRows.length === 0 ? (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={6} color="gray.500" fontSize="xs">
                      No EOB medical necessity denial patterns match the current filters.
                    </Td>
                  </Tr>
                ) : (
                  patternMatrixRows.map((row) => {
                    const isRowSelected =
                      payerFilter === row.payer_name &&
                      cptFilter === row.cpt_code &&
                      displayCodeFilter === row.display_code;
                    return (
                      <Tr
                        key={row.key}
                        cursor="pointer"
                        bg={isRowSelected ? 'blue.50' : undefined}
                        _hover={{ bg: isRowSelected ? 'blue.100' : 'gray.50' }}
                        onClick={() => {
                          if (isRowSelected) {
                            setPayerFilter('ALL');
                            setCptFilter('ALL');
                            setDisplayCodeFilter('ALL');
                          } else {
                            setPayerFilter(row.payer_name);
                            setCptFilter(row.cpt_code);
                            setDisplayCodeFilter(row.display_code);
                          }
                        }}
                      >
                        <Td fontWeight="bold" fontSize="xs" maxW="180px" isTruncated title={row.payer_name}>
                          {row.payer_name}
                        </Td>
                        <Td>
                          <Code fontSize="xs" fontWeight="bold" colorScheme="blue">
                            {row.cpt_code}
                          </Code>
                        </Td>
                        <Td>
                          <Badge colorScheme={row.subtype_badge_color} fontSize="10px">
                            {row.subtype_short_label}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={row.signal_source_color} variant="subtle" fontSize="10px">
                            {row.signal_source_label}
                          </Badge>
                        </Td>
                        <Td>
                          <Code fontSize="11px" colorScheme="purple">
                            {row.display_code}
                          </Code>
                        </Td>
                        <Td maxW="300px" fontSize="11px" color="gray.700">
                          <Text noOfLines={2} title={row.sample_description}>
                            {row.sample_description || '—'}
                          </Text>
                        </Td>
                        <Td>
                          {row.top_pointer_a_code !== 'N/A' ? (
                            <VStack align="flex-start" spacing={0.5}>
                              <Badge colorScheme={row.top_pointer_a_color} fontSize="10px">
                                {row.top_pointer_a_code}
                              </Badge>
                              {row.buriedCount > 0 && (
                                <Badge colorScheme="red" variant="outline" fontSize="9px">
                                  {row.buriedCount} w/ Buried Tier 1–3
                                </Badge>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              —
                            </Text>
                          )}
                        </Td>
                        <Td isNumeric fontSize="xs" fontWeight="extrabold" color="gray.800">
                          {row.denied_lines.toLocaleString()}{' '}
                          <Text as="span" fontWeight="normal" color="gray.500">
                            ({row.claims_count}c)
                          </Text>
                        </Td>
                        <Td isNumeric fontSize="xs" fontWeight="bold" color="red.600">
                          {formatCurrency(row.denied_amount)}
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Table 2: Side-by-Side CPT Modality Breakdown & Billed Pointer A Tier Breakdown */}
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mb={5}>
        {/* Left: CPT Modality Breakdown */}
        <Card variant="outline">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.800">
              By CPT Modality (EOB Adjudicated vs. Med-Nec Denied)
            </Heading>
            <Text fontSize="xs" color="gray.500">
              Compares total EOB-adjudicated lines for each CPT against Medical Necessity denials. Click a CPT row to filter.
            </Text>
          </CardHeader>
          <CardBody pt={0}>
            <TableContainer maxH="320px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>CPT</Th>
                    <Th isNumeric>EOB Adj. Lines</Th>
                    <Th isNumeric>EOB Paid %</Th>
                    <Th isNumeric>Med-Nec Denied (%)</Th>
                    <Th isNumeric>Denied $ (beta)</Th>
                    <Th>Primary Med-Nec Code</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {cptBreakdownRows.map((r) => {
                    const isSelected = cptFilter === r.cpt_code;
                    return (
                      <Tr
                        key={r.cpt_code}
                        cursor="pointer"
                        bg={isSelected ? 'blue.50' : undefined}
                        _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                        onClick={() => setCptFilter(isSelected ? 'ALL' : r.cpt_code)}
                      >
                        <Td>
                          <Code fontSize="xs" fontWeight="bold" colorScheme="blue">
                            {r.cpt_code}
                          </Code>
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {r.eob_adj_lines.toLocaleString()}
                        </Td>
                        <Td isNumeric fontSize="xs" color="green.600" fontWeight="bold">
                          {r.paid_rate_pct}%
                        </Td>
                        <Td isNumeric fontSize="xs" color="red.600" fontWeight="extrabold">
                          {r.med_nec_lines.toLocaleString()} ({r.med_nec_rate_pct}%)
                        </Td>
                        <Td isNumeric fontSize="xs" color="red.600" fontWeight="semibold">
                          {formatCurrency(r.denied_amount)}
                        </Td>
                        <Td fontSize="11px">
                          <Code fontSize="10px">{r.top_display_code}</Code>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        {/* Right: Billed Pointer A Tier Breakdown */}
        <Card variant="outline">
          <CardHeader pb={2}>
            <Heading size="sm" color="gray.800">
              By Billed <Code>Pointer A</Code> (Box 21A) Acuity Tier
            </Heading>
            <Text fontSize="xs" color="gray.500">
              Shows how EOB Medical Necessity denials distribute across the Universal 5-Tier Acuity Hierarchy. Click a tier to filter.
            </Text>
          </CardHeader>
          <CardBody pt={0}>
            <TableContainer maxH="320px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Billed Pointer A Tier</Th>
                    <Th isNumeric>EOB Adj. Claims</Th>
                    <Th isNumeric>Claim Win %</Th>
                    <Th isNumeric>Med-Nec Denied Lines</Th>
                    <Th isNumeric>Buried Tier 1–3 Lines</Th>
                    <Th isNumeric>Denied $ (beta)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tierBreakdownRows.map((t) => {
                    const isSelected = tierFilter === t.tier_key;
                    return (
                      <Tr
                        key={t.tier_key}
                        cursor="pointer"
                        bg={isSelected ? 'blue.50' : undefined}
                        _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                        onClick={() => setTierFilter(isSelected ? 'ALL' : t.tier_key)}
                      >
                        <Td>
                          <Badge colorScheme={t.badge_color} fontSize="10px">
                            {t.short_label}
                          </Badge>
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {t.eob_adj_claims.toLocaleString()}
                        </Td>
                        <Td isNumeric fontSize="xs" color="green.600" fontWeight="bold">
                          {t.claim_win_rate_pct}%
                        </Td>
                        <Td isNumeric fontSize="xs" color="red.600" fontWeight="extrabold">
                          {t.med_nec_lines.toLocaleString()} ({t.med_nec_claims}c)
                        </Td>
                        <Td
                          isNumeric
                          fontSize="xs"
                          fontWeight="bold"
                          color={t.buried_lines > 0 ? 'red.600' : 'gray.400'}
                        >
                          {t.buried_lines > 0 ? t.buried_lines.toLocaleString() : '0'}
                        </Td>
                        <Td isNumeric fontSize="xs" color="red.600" fontWeight="semibold">
                          {formatCurrency(t.denied_amount)}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Table 3: Paginated EOB Line-Level Medical Necessity Explorer */}
      <Card variant="outline">
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <Heading size="sm" color="gray.800">
                EOB Line-Level Medical Necessity Explorer ({filteredLines.length.toLocaleString()} Denied Service Lines)
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Inspect individual EOB denied lines with their exact CARC/RARC/Footnote reason, billed <Code>Pointer A</Code> &amp; <Code>Box 24E</Code> diagnosis pointers, and automated root-cause diagnostic. Click any Claim # to open the full claim timeline.
              </Text>
            </Box>

            {/* Pagination Controls */}
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600">
                Rows:
              </Text>
              <Select
                size="xs"
                w="75px"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </Select>
              <Button
                size="xs"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={safePage <= 1}
              >
                Prev
              </Button>
              <Text fontSize="xs" fontWeight="semibold">
                Page {safePage} of {totalPages}
              </Text>
              <Button
                size="xs"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={safePage >= totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="gray.100">
                <Tr>
                  <Th>Claim # / Case</Th>
                  <Th>Payer &amp; DOS</Th>
                  <Th>CPT / Mod</Th>
                  <Th>Subtype &amp; Signal</Th>
                  <Th>CARC / RARC &amp; EOB Reason Text</Th>
                  <Th>Billed Pointer A &amp; Line Pointers (Box 24E)</Th>
                  <Th>Root-Cause Diagnostic &amp; Action</Th>
                  <Th isNumeric>Denied $ (beta)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedLines.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                      No EOB service lines match the selected Medical Necessity filters.
                    </Td>
                  </Tr>
                ) : (
                  paginatedLines.map((line, idx) => (
                    <Tr
                      key={`${line.claim_number}-${line.cpt_code}-${idx}`}
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Td>
                        <VStack align="flex-start" spacing={0.5}>
                          <HStack spacing={1.5}>
                            <Text
                              as="span"
                              fontSize="xs"
                              fontWeight="extrabold"
                              color="blue.600"
                              cursor="pointer"
                              textDecoration="underline"
                              onClick={() => onOpenClaimByNumber && onOpenClaimByNumber(line.claim_number)}
                            >
                              {line.claim_number}
                            </Text>
                            <Badge
                              colorScheme={line.claim_type === 'pro' ? 'blue' : 'purple'}
                              fontSize="9px"
                            >
                              {String(line.claim_type || '').toUpperCase()}
                            </Badge>
                          </HStack>
                          <Text fontSize="10px" color="gray.500">
                            Case #{line.nma_case_no || 'N/A'}
                          </Text>
                        </VStack>
                      </Td>

                      <Td maxW="175px">
                        <Text fontSize="xs" fontWeight="bold" color="gray.800" isTruncated title={line.payer_name}>
                          {line.payer_name}
                        </Text>
                        <Text fontSize="10px" color="gray.500">
                          DOS: {line.date_of_service || 'N/A'}{' '}
                          {line.place_of_service ? `| POS ${line.place_of_service}` : ''}
                        </Text>
                      </Td>

                      <Td>
                        <VStack align="flex-start" spacing={0.5}>
                          <Code fontSize="xs" fontWeight="bold" colorScheme="blue">
                            {line.cpt_code}
                          </Code>
                          <Text fontSize="10px" color="gray.600">
                            Mod: <b>{line.modifiers_str}</b> ({line.units}u)
                          </Text>
                        </VStack>
                      </Td>

                      <Td>
                        <VStack align="flex-start" spacing={1}>
                          <Badge colorScheme={line.subtype_badge_color} fontSize="9px">
                            {line.subtype_short_label}
                          </Badge>
                          <Badge colorScheme={line.signal_source_color} variant="subtle" fontSize="9px">
                            {line.signal_source_label}
                          </Badge>
                        </VStack>
                      </Td>

                      <Td maxW="290px" whiteSpace="normal">
                        <Code fontSize="10px" colorScheme="purple" mb={0.5} display="inline-block">
                          {line.display_code}
                        </Code>
                        <Text fontSize="11px" color="gray.700" noOfLines={3} title={line.description}>
                          {line.description || '—'}
                        </Text>
                      </Td>

                      <Td maxW="240px" whiteSpace="normal">
                        <VStack align="flex-start" spacing={1}>
                          <HStack spacing={1} wrap="wrap">
                            <Text fontSize="10px" fontWeight="bold" color="gray.600">
                              Ptr A:
                            </Text>
                            <Badge colorScheme={line.pointer_a_badge_color} fontSize="10px">
                              {line.pointer_a_code}
                            </Badge>
                            <Text fontSize="10px" color="gray.500">
                              ({line.pointer_a_tier_label})
                            </Text>
                          </HStack>

                          {line.has_buried_high_acuity && line.buried_high_acuity_code && (
                            <Badge colorScheme="red" variant="solid" fontSize="9px">
                              Buried High-Acuity: {line.buried_high_acuity_code}
                            </Badge>
                          )}

                          {line.line_linked_icds && line.line_linked_icds.length > 0 && (
                            <HStack spacing={1} wrap="wrap">
                              <Text fontSize="10px" color="gray.500">
                                24E:
                              </Text>
                              {line.line_linked_icds.map((dx) => (
                                <Badge
                                  key={`${dx.pointer}-${dx.code}`}
                                  colorScheme={dx.badge_color || 'gray'}
                                  variant="outline"
                                  fontSize="9px"
                                >
                                  {dx.pointer}:{dx.code}
                                </Badge>
                              ))}
                            </HStack>
                          )}
                        </VStack>
                      </Td>

                      <Td maxW="260px" whiteSpace="normal">
                        <VStack align="flex-start" spacing={0.5}>
                          <Badge colorScheme={line.diagnostic_badge_color} fontSize="9px">
                            {line.diagnostic_short_label}
                          </Badge>
                          <Text fontSize="11px" color="gray.700">
                            {line.diagnostic_detail}
                          </Text>
                        </VStack>
                      </Td>

                      <Td isNumeric>
                        <Text fontSize="xs" fontWeight="extrabold" color="red.600">
                          {formatCurrency(line.denied_amount)}
                        </Text>
                        <Text fontSize="10px" color="gray.500">
                          Billed: {formatCurrency(line.billed_amount)}
                        </Text>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
        </>
      )}

      {/* ===================================================================== */}
      {/* SUB-TAB 2: PAYER GROUP & TOP 30 CARRIER SMALL MULTIPLES               */}
      {/* ===================================================================== */}
      {activeSubTab === 'SMALL_MULTIPLES' && (
        <Box>
          <Card variant="outline" mb={4} bg="white" borderColor="gray.200">
            <CardBody py={3} px={4}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <Box>
                  <Heading size="sm" color="gray.800">
                    Payer Group &amp; Top 30 Carrier Weekly EOB Medical Necessity Small Multiples
                  </Heading>
                  <Text fontSize="xs" color="gray.600">
                    Each card shows <b>Med-Nec Denied % (Red Line)</b> and <b>EOB Paid/Allowed % (Green Line)</b> on top, with{' '}
                    <b>Weekly EOB-Adjudicated Line Volume (Bars)</b> underneath. Click any card to filter.
                  </Text>
                </Box>

                <HStack spacing={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                    Group Small Multiples By:
                  </Text>
                  <HStack spacing={1} bg="gray.100" p={1} borderRadius="md">
                    <Button
                      size="xs"
                      colorScheme={smallMultipleViewLevel === 'PAYER_GROUP' ? 'purple' : 'gray'}
                      variant={smallMultipleViewLevel === 'PAYER_GROUP' ? 'solid' : 'ghost'}
                      onClick={() => setSmallMultipleViewLevel('PAYER_GROUP')}
                    >
                      Top-Level Payer Groups ({smallMultipleViewLevel === 'PAYER_GROUP' ? smallMultipleCards.length : 'Top 20'})
                    </Button>
                    <Button
                      size="xs"
                      colorScheme={smallMultipleViewLevel === 'CARRIER' ? 'purple' : 'gray'}
                      variant={smallMultipleViewLevel === 'CARRIER' ? 'solid' : 'ghost'}
                      onClick={() => setSmallMultipleViewLevel('CARRIER')}
                    >
                      Top 30 Specific Carriers
                    </Button>
                  </HStack>
                </HStack>
              </Flex>
            </CardBody>
          </Card>

          {smallMultipleCards.length === 0 ? (
            <Card variant="outline">
              <CardBody py={10} textAlign="center" color="gray.500" fontSize="sm">
                No EOB-adjudicated lines match the selected filters for Small Multiples.
              </CardBody>
            </Card>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {smallMultipleCards.map((card) => {
                const isSelected =
                  smallMultipleViewLevel === 'PAYER_GROUP'
                    ? payerGroupFilter === card.entityName
                    : payerFilter === card.entityName;
                return (
                  <MedNecSmallMultipleCard
                    key={`${smallMultipleViewLevel}-${card.entityName}`}
                    rank={card.rank}
                    entityName={card.entityName}
                    totalEobLines={card.totalEobLines}
                    totalPaidLines={card.totalPaidLines}
                    totalMedNecLines={card.totalMedNecLines}
                    medNecPct={card.medNecPct}
                    paidPct={card.paidPct}
                    weeklySeries={card.weeklySeries}
                    groupType={smallMultipleViewLevel}
                    isSelected={isSelected}
                    onSelectEntity={(ent) => {
                      if (smallMultipleViewLevel === 'PAYER_GROUP') {
                        setPayerGroupFilter(payerGroupFilter === ent ? 'ALL' : ent);
                        setPayerFilter('ALL');
                      } else {
                        setPayerFilter(payerFilter === ent ? 'ALL' : ent);
                      }
                    }}
                  />
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      )}

      {/* ===================================================================== */}
      {/* SUB-TAB 3: INTRAOPERATIVE EEG (95822 VS. 95955) & RULE SCENARIOS      */}
      {/* ===================================================================== */}
      {activeSubTab === 'EEG_SCENARIOS' && (
        <Box>
          <Card variant="outline" mb={4} bg="white" borderColor="gray.200">
            <CardBody py={3} px={4}>
              <Heading size="sm" color="gray.800" mb={1}>
                ⚡ Intraoperative EEG (<Code>95822</Code> vs. <Code>95955</Code>) Adjudication &amp;{' '}
                <Code>use_95822_for_eeg</Code> Rule Analysis
              </Heading>
              <Text fontSize="xs" color="gray.600">
                Compares <b>95822 (Sleep/Coma EEG)</b> vs. <b>95955 (Non-Intracranial EEG)</b> across all 9 Payer/Rule Scenarios—including the{' '}
                <b>UHC/AARP Medicare Advantage Rules #14/#24 leak (84.5% denied via PI-16/M51)</b>,{' '}
                <b>UHC Commercial (Rule #34 ended Oct 16)</b>,{' '}
                <b>WellMed / Intermountain / Humana / Aetna MA (90%+ paid on 95822)</b>, and{' '}
                <b>Aetna &amp; Cigna Commercial (80.9% denied on 95955)</b>.
              </Text>
            </CardBody>
          </Card>

          {/* 1. Top 4 EEG Scenario KPI Cards */}
          <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={5}>
            <Card variant="outline" borderTop="3px solid" borderTopColor="red.500" bg="white">
              <CardBody py={3} px={4}>
                <Badge colorScheme="red" fontSize="9px" mb={1}>
                  1. UHC/AARP MA 95822 Denied % (Rule #14/#24 Leak)
                </Badge>
                <Text fontSize="2xl" fontWeight="extrabold" color="red.600">
                  {eegAnalyticsComputed.kpis.aarp.denPct}% Denied
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color="gray.700" mt={0.5}>
                  {eegAnalyticsComputed.kpis.aarp.den}/{eegAnalyticsComputed.kpis.aarp.tot} lines (
                  {formatCurrency(eegAnalyticsComputed.kpis.aarp.denDollars)} denied)
                </Text>
                <Badge colorScheme="red" variant="subtle" fontSize="9px" mt={1}>
                  STILL ACTIVE — Carve Out UHC/AARP
                </Badge>
              </CardBody>
            </Card>

            <Card variant="outline" borderTop="3px solid" borderTopColor="orange.500" bg="white">
              <CardBody py={3} px={4}>
                <Badge colorScheme="orange" fontSize="9px" mb={1}>
                  2. UHC Commercial 95822 Denied % (Rule #34 Ended Oct 16)
                </Badge>
                <Text fontSize="2xl" fontWeight="extrabold" color="orange.600">
                  {eegAnalyticsComputed.kpis.ucomm.denPct}% Denied
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color="gray.700" mt={0.5}>
                  {eegAnalyticsComputed.kpis.ucomm.den}/{eegAnalyticsComputed.kpis.ucomm.tot} lines (
                  {formatCurrency(eegAnalyticsComputed.kpis.ucomm.denDollars)} denied)
                </Text>
                <Badge colorScheme="orange" variant="subtle" fontSize="9px" mt={1}>
                  Rule #34 Ended Oct 16 (End HMO Rule #229)
                </Badge>
              </CardBody>
            </Card>

            <Card variant="outline" borderTop="3px solid" borderTopColor="green.500" bg="white">
              <CardBody py={3} px={4}>
                <Badge colorScheme="green" fontSize="9px" mb={1}>
                  3. WellMed / Intermountain / OptumCare 95822 Paid %
                </Badge>
                <Text fontSize="2xl" fontWeight="extrabold" color="green.600">
                  {eegAnalyticsComputed.kpis.udel.paidPct}% Paid
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color="gray.700" mt={0.5}>
                  {eegAnalyticsComputed.kpis.udel.paid}/{eegAnalyticsComputed.kpis.udel.tot} lines paid/allowed
                </Text>
                <Badge colorScheme="green" variant="subtle" fontSize="9px" mt={1}>
                  Must Keep 95822 on Delegated MA!
                </Badge>
              </CardBody>
            </Card>

            <Card variant="outline" borderTop="3px solid" borderTopColor="purple.500" bg="white">
              <CardBody py={3} px={4}>
                <Badge colorScheme="purple" fontSize="9px" mb={1}>
                  4. Aetna &amp; Cigna Commercial 95955 Denied %
                </Badge>
                <Text fontSize="2xl" fontWeight="extrabold" color="red.600">
                  {eegAnalyticsComputed.kpis.aetCig.denPct}% Denied
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color="gray.700" mt={0.5}>
                  {eegAnalyticsComputed.kpis.aetCig.den}/{eegAnalyticsComputed.kpis.aetCig.tot} lines (
                  {formatCurrency(eegAnalyticsComputed.kpis.aetCig.denDollars)} denied)
                </Text>
                <Badge colorScheme="purple" variant="subtle" fontSize="9px" mt={1}>
                  Add use_95822_for_eeg for Aetna &amp; Cigna
                </Badge>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* 2. Concrete Rule Change Recommendations & Expected Impact Table */}
          <Card variant="outline" mb={5} bg="white">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <Box>
                  <Heading size="sm" color="gray.800">
                    🎯 Concrete Rule Change Recommendations &amp; Expected Impact (Calculated Live from Dataset)
                  </Heading>
                  <Text fontSize="xs" color="gray.500">
                    Quantifies exact denied lines eliminated and projected cash recovery for each recommended{' '}
                    <Code fontSize="10px">deterministic_rules</Code> update.
                  </Text>
                </Box>
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant={showEegSql ? 'solid' : 'outline'}
                  onClick={() => setShowEegSql((v) => !v)}
                >
                  {showEegSql ? 'Hide SQL Migration' : '📋 View Ready-to-Run SQL Migration'}
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              <Collapse in={showEegSql} animateOpacity>
                <Box mb={4} p={3} bg="gray.900" color="gray.100" borderRadius="md" position="relative">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="xs" fontWeight="bold" color="blue.200">
                      Ready-to-Run PostgreSQL Migration (deterministic_rules)
                    </Text>
                    <Button
                      size="xs"
                      colorScheme={sqlCopied ? 'green' : 'gray'}
                      onClick={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(eegAnalyticsComputed.sqlMigration);
                          setSqlCopied(true);
                          setTimeout(() => setSqlCopied(false), 2000);
                        }
                      }}
                    >
                      {sqlCopied ? '✓ Copied SQL' : 'Copy SQL'}
                    </Button>
                  </Flex>
                  <Box as="pre" fontSize="11px" overflowX="auto" whiteSpace="pre-wrap">
                    {eegAnalyticsComputed.sqlMigration}
                  </Box>
                </Box>
              </Collapse>

              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>Priority &amp; Target Segment</Th>
                      <Th>Current State in Prod DB</Th>
                      <Th>Recommended Rule Change</Th>
                      <Th>Denied Lines Eliminated</Th>
                      <Th>Denied Billed $ Eliminated (beta)</Th>
                      <Th>Expected Cash / Revenue Impact</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eegAnalyticsComputed.recImpactRows.map((row) => (
                      <Tr key={row.priority} _hover={{ bg: 'gray.50' }}>
                        <Td fontWeight="bold" fontSize="xs" whiteSpace="normal" minW="170px">
                          {row.priority}
                        </Td>
                        <Td fontSize="xs" color="gray.700" whiteSpace="normal" minW="210px">
                          {row.currentState}
                        </Td>
                        <Td fontSize="xs" color="blue.800" fontWeight="medium" whiteSpace="normal" minW="240px">
                          {row.recommendedChange}
                        </Td>
                        <Td fontSize="xs" fontWeight="bold" color="red.600" whiteSpace="normal" minW="140px">
                          {row.deniedLinesEliminated}
                        </Td>
                        <Td fontSize="xs" fontWeight="bold" color="red.600" whiteSpace="normal" minW="120px">
                          {row.deniedDollarsEliminated}
                        </Td>
                        <Td fontSize="xs" color="gray.800" whiteSpace="normal" minW="260px">
                          {row.expectedImpact}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>

          {/* 3. Weekly Trends 3x3 Small-Multiples Grid Across All 9 EEG Scenarios */}
          <Box mb={5}>
            <Heading size="sm" color="gray.800" mb={1}>
              📈 Weekly EEG Adjudication &amp; Med-Nec Denial Trends Across All 9 Scenarios (
              {dateMode === 'EOB_WEEK' ? 'EOB Remittance Week' : 'Surgery DOS Week'})
            </Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              Each scenario card plots <b>EEG Med-Nec Denied % (Red Solid)</b>, <b>All EEG Denied % (Orange Dashed)</b>, and{' '}
              <b>EEG Paid/Allowed % (Green Solid)</b> on top, with{' '}
              <b>Weekly EOB Line Volume stacked by 95822 (Blue) vs. 95955 (Purple)</b> underneath.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {eegAnalyticsComputed.scenarioCards.map((sc) => (
                <EEGScenarioCard
                  key={sc.scenarioName}
                  idx={sc.idx}
                  scenarioName={sc.scenarioName}
                  totalLines={sc.totalLines}
                  paidPct={sc.paidPct}
                  medNecPct={sc.medNecPct}
                  weeklySeries={sc.weeklySeries}
                  note={sc.note}
                />
              ))}
            </SimpleGrid>
          </Box>

          {/* 4. Head-to-Head 9-Scenario Matrix: 95822 vs. 95955 Performance & Companion Claim Impact */}
          <Card variant="outline" mb={5} bg="white">
            <CardHeader pb={2}>
              <Heading size="sm" color="gray.800">
                📊 Head-to-Head Scenario Matrix: <Code>95822</Code> vs. <Code>95955</Code> Performance &amp; Companion Claim Impact
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Compares 95822 and 95955 line volume, paid %, Medical Necessity denial %, and how often 95822 was denied on a claim where companion IONM CPTs were paid. Click a scenario row to filter the Carrier breakdown below.
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>EEG Scenario</Th>
                      <Th isNumeric>95822 Lines</Th>
                      <Th>95822 Paid %</Th>
                      <Th>95822 Med-Nec Denied %</Th>
                      <Th isNumeric>95822 Denied on Paid Claim</Th>
                      <Th isNumeric>95955 Lines</Th>
                      <Th>95955 Paid %</Th>
                      <Th>95955 Med-Nec Denied %</Th>
                      <Th isNumeric>Total Denied $ (beta)</Th>
                      <Th>Top Denial Codes</Th>
                      <Th>Rule Status &amp; Recommendation</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eegAnalyticsComputed.scenMatrixRows.map((r) => {
                      const isSelected = selectedEegScenario === r.scenarioName;
                      return (
                        <Tr
                          key={r.scenarioName}
                          cursor="pointer"
                          bg={isSelected ? 'blue.50' : undefined}
                          _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                          onClick={() =>
                            setSelectedEegScenario(isSelected ? 'ALL_SCENARIOS' : r.scenarioName)
                          }
                        >
                          <Td fontWeight="bold" fontSize="xs" whiteSpace="normal" minW="190px">
                            {r.scenarioName}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold">
                            {r.l822.toLocaleString()}
                          </Td>
                          <Td fontSize="xs" color="green.600" fontWeight="bold" whiteSpace="nowrap">
                            {r.paid822Str}
                          </Td>
                          <Td fontSize="xs" color="red.600" fontWeight="bold" whiteSpace="nowrap">
                            {r.mn822Str}
                          </Td>
                          <Td
                            isNumeric
                            fontSize="xs"
                            fontWeight="extrabold"
                            color={r.compPaid822 > 0 ? 'red.600' : 'gray.500'}
                          >
                            {r.compPaid822}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold">
                            {r.l955.toLocaleString()}
                          </Td>
                          <Td fontSize="xs" color="green.600" fontWeight="bold" whiteSpace="nowrap">
                            {r.paid955Str}
                          </Td>
                          <Td fontSize="xs" color="red.600" fontWeight="bold" whiteSpace="nowrap">
                            {r.mn955Str}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="bold" color="red.600">
                            {formatCurrency(r.totalDeniedDollars)}
                          </Td>
                          <Td fontSize="11px" whiteSpace="normal" minW="130px">
                            <Code fontSize="10px" colorScheme="purple">
                              {r.topCodes}
                            </Code>
                          </Td>
                          <Td fontSize="11px" color="gray.700" whiteSpace="normal" minW="260px">
                            {r.note}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>

          {/* 5. Live DB deterministic_rules Audit Table & Carrier Breakdown by EEG Scenario */}
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            {/* Left: Live DB deterministic_rules Audit */}
            <Card variant="outline" bg="white">
              <CardHeader pb={2}>
                <Heading size="sm" color="gray.800">
                  ⚙️ Live Production <Code>deterministic_rules</Code> Audit for EEG
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Shows all active and ended EEG &amp; 95822/95955 NCCI modifier rules in PostgreSQL.
                </Text>
              </CardHeader>
              <CardBody pt={0}>
                <TableContainer maxH="380px" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Rule ID</Th>
                        <Th>Status</Th>
                        <Th>Type / Target</Th>
                        <Th>Action</Th>
                        <Th>Effective Dates &amp; Conditions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {eegAnalyticsComputed.dbRules.length === 0 ? (
                        <Tr>
                          <Td colSpan={5} textAlign="center" py={6} color="gray.500" fontSize="xs">
                            No EEG deterministic_rules rows loaded.
                          </Td>
                        </Tr>
                      ) : (
                        eegAnalyticsComputed.dbRules.map((dr) => (
                          <Tr key={dr.id} _hover={{ bg: 'gray.50' }}>
                            <Td fontWeight="bold" fontSize="xs">
                              #{dr.id}
                            </Td>
                            <Td>
                              <Badge colorScheme={dr.status === 'ACTIVE' ? 'green' : 'gray'} fontSize="9px">
                                {dr.status}
                              </Badge>
                            </Td>
                            <Td fontSize="xs" whiteSpace="normal">
                              <Badge colorScheme="blue" variant="outline" fontSize="9px" mr={1}>
                                {dr.rule_type}
                              </Badge>
                              <b>{dr.target_name}</b>
                            </Td>
                            <Td>
                              <Code fontSize="10px" colorScheme="purple">
                                {dr.action}
                              </Code>
                            </Td>
                            <Td fontSize="10px" color="gray.600" whiteSpace="normal" minW="180px">
                              <Box>
                                Start: {dr.effective_start || '—'}
                                {dr.effective_end ? ` | End: ${dr.effective_end}` : ''}
                              </Box>
                              {dr.conditions_json && dr.conditions_json !== 'null' && (
                                <Code fontSize="9px" display="block" mt={0.5} whiteSpace="pre-wrap">
                                  {dr.conditions_json}
                                </Code>
                              )}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>

            {/* Right: Carrier-Level Breakdown Inside Selected EEG Scenario */}
            <Card variant="outline" bg="white">
              <CardHeader pb={2}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <Box>
                    <Heading size="sm" color="gray.800">
                      🔍 Carrier × CPT Breakdown by EEG Scenario
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      Drill into specific carriers and plans within each EEG rule scenario.
                    </Text>
                  </Box>
                  <Select
                    size="xs"
                    w="240px"
                    bg="white"
                    value={selectedEegScenario}
                    onChange={(e) => setSelectedEegScenario(e.target.value)}
                  >
                    <option value="ALL_SCENARIOS">All 9 EEG Scenarios</option>
                    {eegAnalyticsComputed.scenarioOrder.map((sc) => (
                      <option key={sc} value={sc}>
                        {sc}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </CardHeader>
              <CardBody pt={0}>
                <TableContainer maxH="380px" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Carrier / Plan</Th>
                        <Th>CPT</Th>
                        <Th isNumeric>EOB Lines</Th>
                        <Th isNumeric>Paid %</Th>
                        <Th isNumeric>Med-Nec %</Th>
                        <Th isNumeric>Denied on Paid Claim</Th>
                        <Th isNumeric>Denied $ (beta)</Th>
                        <Th>Top Codes</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {eegAnalyticsComputed.carrierBreakdownRows.length === 0 ? (
                        <Tr>
                          <Td colSpan={8} textAlign="center" py={6} color="gray.500" fontSize="xs">
                            No EEG lines match the selected scenario filter.
                          </Td>
                        </Tr>
                      ) : (
                        eegAnalyticsComputed.carrierBreakdownRows.map((row) => (
                          <Tr key={row.key} _hover={{ bg: 'gray.50' }}>
                            <Td fontSize="xs" fontWeight="bold" whiteSpace="normal" minW="150px">
                              {row.carrier_name}
                              <Text fontSize="9px" color="gray.500" fontWeight="normal">
                                {row.eeg_scenario}
                              </Text>
                            </Td>
                            <Td>
                              <Code
                                fontSize="xs"
                                fontWeight="bold"
                                colorScheme={row.cpt_code === '95822' ? 'blue' : 'purple'}
                              >
                                {row.cpt_code}
                              </Code>
                            </Td>
                            <Td isNumeric fontSize="xs" fontWeight="bold">
                              {row.eob_lines}
                            </Td>
                            <Td isNumeric fontSize="xs" color="green.600" fontWeight="bold">
                              {row.paid_pct}% ({row.paid_lines})
                            </Td>
                            <Td isNumeric fontSize="xs" color="red.600" fontWeight="bold">
                              {row.med_nec_pct}% ({row.med_nec_denied})
                            </Td>
                            <Td
                              isNumeric
                              fontSize="xs"
                              fontWeight="extrabold"
                              color={row.denied_on_paid_claim > 0 ? 'red.600' : 'gray.400'}
                            >
                              {row.denied_on_paid_claim}
                            </Td>
                            <Td isNumeric fontSize="xs" color="red.600" fontWeight="semibold">
                              {formatCurrency(row.denied_dollars)}
                            </Td>
                            <Td fontSize="10px" whiteSpace="normal">
                              <Code fontSize="9px">{row.top_denial_codes}</Code>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
});

export default NMAMedicalNecessityTab;
